/*
  Storefront API cart helpers.
  Load this file where needed, then use window.AbokStorefrontCart.

  Notes:
  - This operates on Shopify Storefront API carts, not the theme Ajax cart.
  - Use a public Storefront API token only. Never expose Admin API tokens in browser code.
*/
(function (global) {
  'use strict';

  const DEFAULT_ACCESS_TOKEN = 'b5f5879c29bf9a8de4b6efdbe53fcabb';
  const DEFAULT_API_VERSION = '2026-04';
  const DEFAULT_STORAGE_KEY = 'abok_storefront_cart_id';
  const CART_CURRENCY_COOKIE = 'cart_currency';
  const SHOPIFY_GID_PREFIX = 'gid://shopify/';
  const MAX_QUANTITY_ERROR_MESSAGE = 'The maximum quantity of this item is already in your cart.';

  let localizationRequest = null;
  const cartCurrencySyncRequests = new Map();

  const settings = {
    accessToken: '',
    apiVersion: DEFAULT_API_VERSION,
    shopDomain: '',
    storageKey: DEFAULT_STORAGE_KEY,
    debug: false
  };

  const CART_FRAGMENT = `
    fragment CartFields on Cart {
      id
      checkoutUrl
      createdAt
      updatedAt
      totalQuantity
      note
      buyerIdentity {
        countryCode
      }
      attributes {
        key
        value
      }
      discountCodes {
        code
        applicable
      }
      discountAllocations {
        discountedAmount {
          amount
          currencyCode
        }
        ... on CartAutomaticDiscountAllocation {
          title
        }
        ... on CartCodeDiscountAllocation {
          code
        }
        ... on CartCustomDiscountAllocation {
          title
        }
      }
      cost {
        checkoutChargeAmount {
          amount
          currencyCode
        }
        subtotalAmount {
          amount
          currencyCode
        }
        subtotalAmountEstimated
        totalAmount {
          amount
          currencyCode
        }
        totalAmountEstimated
      }
      lines(first: 250) {
        edges {
          node {
            id
            quantity
            attributes {
              key
              value
            }
            discountAllocations {
              discountedAmount {
                amount
                currencyCode
              }
              ... on CartAutomaticDiscountAllocation {
                title
              }
              ... on CartCodeDiscountAllocation {
                code
              }
              ... on CartCustomDiscountAllocation {
                title
              }
            }
            cost {
              amountPerQuantity {
                amount
                currencyCode
              }
              subtotalAmount {
                amount
                currencyCode
              }
              totalAmount {
                amount
                currencyCode
              }
            }
            merchandise {
              ... on ProductVariant {
                id
                title
                sku
                availableForSale
                image {
                  url
                  altText
                  width
                  height
                }
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                quantityAvailable
                selectedOptions {
                  name
                  value
                }
                product {
                  id
                  handle
                  title
                  vendor
                }
              }
            }
          }
        }
      }
    }
  `;

  const CART_QUERY = `
    ${CART_FRAGMENT}
    query getCart($cartId: ID!) {
      cart(id: $cartId) {
        ...CartFields
      }
    }
  `;

  const LOCALIZATION_QUERY = `
    query getCartCurrencyLocalization {
      localization {
        country {
          isoCode
          currency {
            isoCode
          }
        }
        availableCountries {
          isoCode
          currency {
            isoCode
          }
        }
      }
    }
  `;

  const CART_CREATE_MUTATION = `
    ${CART_FRAGMENT}
    mutation createCart($input: CartInput) {
      cartCreate(input: $input) {
        cart {
          ...CartFields
        }
        userErrors {
          field
          message
          code
        }
      }
    }
  `;

  const CART_LINES_ADD_MUTATION = `
    ${CART_FRAGMENT}
    mutation addCartLines($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart {
          ...CartFields
        }
        userErrors {
          field
          message
          code
        }
      }
    }
  `;

  const CART_LINES_UPDATE_MUTATION = `
    ${CART_FRAGMENT}
    mutation updateCartLines($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart {
          ...CartFields
        }
        userErrors {
          field
          message
          code
        }
      }
    }
  `;

  const CART_LINES_REMOVE_MUTATION = `
    ${CART_FRAGMENT}
    mutation removeCartLines($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart {
          ...CartFields
        }
        userErrors {
          field
          message
          code
        }
      }
    }
  `;

  const CART_DISCOUNT_CODES_UPDATE_MUTATION = `
    ${CART_FRAGMENT}
    mutation updateCartDiscountCodes($cartId: ID!, $discountCodes: [String!]!) {
      cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
        cart {
          ...CartFields
        }
        userErrors {
          field
          message
          code
        }
      }
    }
  `;

  const CART_ATTRIBUTES_UPDATE_MUTATION = `
    ${CART_FRAGMENT}
    mutation updateCartAttributes($cartId: ID!, $attributes: [AttributeInput!]!) {
      cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
        cart {
          ...CartFields
        }
        userErrors {
          field
          message
          code
        }
      }
    }
  `;

  const CART_NOTE_UPDATE_MUTATION = `
    ${CART_FRAGMENT}
    mutation updateCartNote($cartId: ID!, $note: String!) {
      cartNoteUpdate(cartId: $cartId, note: $note) {
        cart {
          ...CartFields
        }
        userErrors {
          field
          message
          code
        }
      }
    }
  `;

  const CART_BUYER_IDENTITY_UPDATE_MUTATION = `
    ${CART_FRAGMENT}
    mutation updateCartBuyerIdentity($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
      cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
        cart {
          ...CartFields
        }
        userErrors {
          field
          message
          code
        }
      }
    }
  `;

  class StorefrontCartError extends Error {
    constructor(message, details) {
      super(message);
      this.name = 'StorefrontCartError';
      this.details = details || {};
    }
  }

  function getMetaContent(name) {
    if (typeof document === 'undefined') return '';

    return document.querySelector(`meta[name="${name}"]`)?.content || '';
  }

  function resolveShopDomain() {
    return settings.shopDomain
      || getMetaContent('shop-url')
      || (global.Shopify && global.Shopify.shop ? `https://${global.Shopify.shop}` : '')
      || (global.theme && global.theme.routes ? global.theme.routes.shop_url : '')
      || global.location?.origin
      || '';
  }

  function resolveAccessToken() {
    return settings.accessToken
      || getMetaContent('storefront-access-token')
      || global.theme?.storefront?.accessToken
      || global.theme?.settings?.storefrontApiToken
      || DEFAULT_ACCESS_TOKEN;
  }

  function normalizeOrigin(shopDomain) {
    const domain = String(shopDomain || '').trim().replace(/\/+$/, '');
    if (!domain) {
      throw new StorefrontCartError('Shop domain is missing.');
    }

    return /^https?:\/\//i.test(domain) ? domain : `https://${domain}`;
  }

  function getGraphqlEndpoint() {
    return `${normalizeOrigin(resolveShopDomain())}/api/${settings.apiVersion}/graphql.json`;
  }

  function readCookie(name) {
    if (typeof document === 'undefined') return '';

    const encodedName = encodeURIComponent(name);
    const value = document.cookie
      .split(';')
      .map((cookie) => cookie.trim())
      .find((cookie) => cookie.startsWith(`${encodedName}=`));

    if (!value) return '';

    try {
      return decodeURIComponent(value.slice(encodedName.length + 1));
    }
    catch (error) {
      return value.slice(encodedName.length + 1);
    }
  }

  function readCartCurrency() {
    const currencyCode = readCookie(CART_CURRENCY_COOKIE).trim().toUpperCase();
    return /^[A-Z]{3}$/.test(currencyCode) ? currencyCode : '';
  }

  function getMoneyCurrencyCode(cart) {
    return String(
      cart?.cost?.totalAmount?.currencyCode
      || cart?.cost?.subtotalAmount?.currencyCode
      || ''
    ).toUpperCase();
  }

  async function getLocalization(options) {
    if (!localizationRequest) {
      localizationRequest = graphql(LOCALIZATION_QUERY, {}, options)
        .then((data) => data?.localization || null)
        .catch((error) => {
          localizationRequest = null;
          throw error;
        });
    }

    return localizationRequest;
  }

  async function resolveCountryCodeForCurrency(currencyCode, options) {
    if (!currencyCode) return '';

    // Shopify Cart pricing is country-contextual, so map the currency cookie to an enabled Market country.
    const localization = await getLocalization(options);
    const availableCountries = localization?.availableCountries || [];
    const preferredCountryCode = String(global.Shopify?.country || '').trim().toUpperCase();
    const currentCountry = localization?.country || null;
    const matchesCurrency = (country) => String(country?.currency?.isoCode || '').toUpperCase() === currencyCode;

    const preferredCountry = availableCountries.find((country) => (
      String(country?.isoCode || '').toUpperCase() === preferredCountryCode
      && matchesCurrency(country)
    ));
    const matchingCountry = preferredCountry
      || (matchesCurrency(currentCountry) ? currentCountry : null)
      || availableCountries.find(matchesCurrency);

    return String(matchingCountry?.isoCode || '').toUpperCase();
  }

  async function applyCartCurrencyToInput(input, options) {
    const cartInput = input || {};
    const currencyCode = readCartCurrency();
    if (!currencyCode) return cartInput;

    try {
      const countryCode = await resolveCountryCodeForCurrency(currencyCode, options);
      if (!countryCode) {
        log(`No enabled Shopify Market uses the ${currencyCode} cart currency.`);
        return cartInput;
      }

      return {
        ...cartInput,
        buyerIdentity: {
          ...(cartInput.buyerIdentity || {}),
          countryCode
        }
      };
    }
    catch (error) {
      log('Unable to resolve the cart currency before creating the cart.', error);
      return cartInput;
    }
  }

  async function synchronizeCartCurrency(cart, options) {
    const currencyCode = readCartCurrency();
    if (!cart?.id || !currencyCode || getMoneyCurrencyCode(cart) === currencyCode) return cart;

    const requestKey = `${cart.id}:${currencyCode}`;
    if (cartCurrencySyncRequests.has(requestKey)) {
      return cartCurrencySyncRequests.get(requestKey);
    }

    const syncRequest = (async () => {
      const countryCode = await resolveCountryCodeForCurrency(currencyCode, options);
      if (!countryCode) {
        log(`No enabled Shopify Market uses the ${currencyCode} cart currency.`);
        return cart;
      }

      const data = await graphql(CART_BUYER_IDENTITY_UPDATE_MUTATION, {
        cartId: cart.id,
        buyerIdentity: { countryCode }
      }, options);
      const updatedCart = getMutationPayload(data, 'cartBuyerIdentityUpdate');

      dispatchCartEvent('currency-updated', {
        cart: updatedCart,
        currencyCode,
        countryCode
      });
      return updatedCart;
    })()
      .catch((error) => {
        log('Unable to synchronize the Storefront cart currency.', error);
        return cart;
      })
      .finally(() => {
        cartCurrencySyncRequests.delete(requestKey);
      });

    cartCurrencySyncRequests.set(requestKey, syncRequest);
    return syncRequest;
  }

  function readStoredCartId() {
    try {
      return global.localStorage?.getItem(settings.storageKey) || '';
    }
    catch (error) {
      return '';
    }
  }

  function writeStoredCartId(cartId) {
    if (!cartId) return;

    try {
      global.localStorage?.setItem(settings.storageKey, cartId);
    }
    catch (error) {
      log('Unable to persist Storefront cart ID.', error);
    }
  }

  function clearStoredCartId() {
    try {
      global.localStorage?.removeItem(settings.storageKey);
    }
    catch (error) {
      log('Unable to clear Storefront cart ID.', error);
    }
  }

  function log() {
    if (!settings.debug || !global.console) return;
    global.console.log('[AbokStorefrontCart]', ...arguments);
  }

  function toArray(value) {
    if (value === undefined || value === null) return [];
    return Array.isArray(value) ? value : [value];
  }

  function toPositiveInteger(value, fallback) {
    const number = Number.parseInt(value, 10);
    if (Number.isNaN(number)) return fallback;

    return Math.max(number, 0);
  }

  function toShopifyGid(value, type) {
    const text = String(value || '').trim();
    if (!text) {
      throw new StorefrontCartError(`${type} ID is required.`);
    }

    if (text.startsWith(SHOPIFY_GID_PREFIX)) return text;
    if (/^\d+$/.test(text)) return `${SHOPIFY_GID_PREFIX}${type}/${text}`;

    return text;
  }

  function normalizeAttributes(attributes) {
    if (!attributes) return undefined;

    const entries = Array.isArray(attributes)
      ? attributes
      : Object.entries(attributes).map(([key, value]) => ({ key, value }));

    return entries
      .filter((attribute) => attribute && attribute.key !== undefined && attribute.value !== undefined)
      .map((attribute) => ({
        key: String(attribute.key),
        value: String(attribute.value)
      }));
  }

  function normalizeAddLine(line) {
    if (!line || typeof line !== 'object') {
      throw new StorefrontCartError('Cart line must be an object.');
    }

    const normalized = {
      merchandiseId: toShopifyGid(line.merchandiseId || line.variantId || line.id, 'ProductVariant'),
      quantity: toPositiveInteger(line.quantity, 1) || 1
    };

    if (line.sellingPlanId) {
      normalized.sellingPlanId = toShopifyGid(line.sellingPlanId, 'SellingPlan');
    }

    ['_giftLine', '_giftMainLine', '_giftGroup', '_giftCoupon', '_giftParentVariantId', '_giftParentProductId'].forEach((key) => {
      if (line[key] !== undefined) normalized[key] = line[key];
    });

    return normalized;
  }

  function normalizeUpdateLine(line) {
    if (!line || typeof line !== 'object') {
      throw new StorefrontCartError('Cart line update must be an object.');
    }

    const normalized = {
      id: String(line.lineId || line.id || '').trim()
    };

    if (!normalized.id) {
      throw new StorefrontCartError('Cart line ID is required.');
    }

    if (line.quantity !== undefined) {
      normalized.quantity = toPositiveInteger(line.quantity, 0);
    }

    if (line.merchandiseId || line.variantId) {
      normalized.merchandiseId = toShopifyGid(line.merchandiseId || line.variantId, 'ProductVariant');
    }

    if (line.sellingPlanId !== undefined) {
      normalized.sellingPlanId = line.sellingPlanId ? toShopifyGid(line.sellingPlanId, 'SellingPlan') : null;
    }

    return normalized;
  }

  function normalizeCartInput(input) {
    const cartInput = { ...(input || {}) };

    if (cartInput.lines) {
      cartInput.lines = toArray(cartInput.lines).map(normalizeAddLine);
    }

    if (cartInput.discountCodes) {
      cartInput.discountCodes = toArray(cartInput.discountCodes).map(String).filter(Boolean);
    }

    return cartInput;
  }

  function formatApiErrors(errors) {
    return toArray(errors)
      .map((error) => {
        if (!error) return '';

        const field = Array.isArray(error.field) ? error.field.join('.') : error.field;
        const code = error.code ? ` (${error.code})` : '';
        return `${field ? `${field}: ` : ''}${error.message || 'Unknown Storefront API error'}${code}`;
      })
      .filter(Boolean)
      .join('; ');
  }

  function getMutationPayload(data, key) {
    const payload = data && data[key];
    if (!payload) {
      throw new StorefrontCartError(`Missing Storefront API payload: ${key}.`, { data });
    }

    const userErrors = payload.userErrors || payload.cartUserErrors || [];
    if (userErrors.length) {
      throw new StorefrontCartError(formatApiErrors(userErrors), { userErrors, payload });
    }

    if (!payload.cart) {
      throw new StorefrontCartError('Storefront API did not return a cart.', { payload });
    }

    persistCart(payload.cart);
    return payload.cart;
  }

  function persistCart(cart) {
    if (cart && cart.id) {
      writeStoredCartId(cart.id);
    }

    return cart;
  }

  function getCartLines(cart) {
    return cart?.lines?.edges?.map((edge) => edge.node).filter(Boolean) || [];
  }

  function findLine(cart, lineId) {
    const id = String(lineId || '').trim();
    return getCartLines(cart).find((line) => line.id === id) || null;
  }

  function findLineByVariantId(cart, variantId, matcher) {
    const merchandiseId = toShopifyGid(variantId, 'ProductVariant');

    return getCartLines(cart).find((line) => {
      if (line?.merchandise?.id !== merchandiseId) return false;
      return typeof matcher === 'function' ? matcher(line) : true;
    }) || null;
  }

  function getAvailableQuantity(value) {
    const quantity = Number.parseInt(value, 10);
    return Number.isFinite(quantity) ? quantity : null;
  }

  function getCartVariantQuantity(cart, variantId, excludeLineId) {
    const merchandiseId = toShopifyGid(variantId, 'ProductVariant');

    return getCartLines(cart).reduce((total, line) => {
      if (excludeLineId && line.id === excludeLineId) return total;
      if (line?.merchandise?.id !== merchandiseId) return total;
      return total + (Number.parseInt(line.quantity || '0', 10) || 0);
    }, 0);
  }

  function getVariantAvailabilityFromCart(cart, variantId) {
    const merchandiseId = toShopifyGid(variantId, 'ProductVariant');
    const line = getCartLines(cart).find((cartLine) => cartLine?.merchandise?.id === merchandiseId);
    if (!line?.merchandise) return null;

    const available = getAvailableQuantity(line.merchandise.quantityAvailable);
    if (available !== null) return available;
    if (line.merchandise.availableForSale === false) return Number.parseInt(line.quantity || '0', 10) || 0;
    return null;
  }

  function getAttributeValue(attributes, key) {
    return (attributes || []).find((attribute) => attribute?.key === key)?.value || '';
  }

  function getVariantNumericId(variantGid) {
    return String(variantGid || '').split('/').pop();
  }

  function getProductNumericId(productGid) {
    return String(productGid || '').split('/').pop();
  }

  function isGiftAddLine(line) {
    return Boolean(line?._giftLine);
  }

  function isGiftCartLine(line) {
    return Boolean(getAttributeValue(line?.attributes, '_coupon') || getAttributeValue(line?.attributes, '_product'));
  }

  function hasDiscountAllocation(line) {
    return Array.isArray(line?.discountAllocations) && line.discountAllocations.length > 0;
  }

  function hasZeroDiscountAllocation(line, code) {
    const targetCode = String(code || '').trim();
    if (!targetCode) return false;

    return (line?.discountAllocations || []).some((discount) => {
      const discountCode = String(discount?.code || '').trim();
      const amount = Number.parseFloat(discount?.discountedAmount?.amount || '0');
      return discountCode === targetCode && amount === 0;
    });
  }

  function isDiscountedFreeLine(line) {
    const amount = Number.parseFloat(line?.cost?.totalAmount?.amount || '0');
    return amount === 0 && hasDiscountAllocation(line);
  }

  function isGiftParentCartLine(line, coupon) {
    return hasZeroDiscountAllocation(line, coupon) && !isGiftCartLine(line) && !isDiscountedFreeLine(line);
  }

  function findMainLineByGiftState(cart, addLine, shouldHaveGift) {
    if (!cart || isGiftAddLine(addLine) || addLine.sellingPlanId) return null;
    const addVariantId = getVariantNumericId(addLine.merchandiseId);
    const addProductId = getProductNumericId(addLine._giftParentProductId);

    return getCartLines(cart).find((line) => {
      if (isGiftCartLine(line)) return false;
      if (isDiscountedFreeLine(line)) return false;
      if (isGiftParentCartLine(line, addLine._giftCoupon) !== shouldHaveGift) return false;
      if (shouldHaveGift && addProductId) {
        return getProductNumericId(line?.merchandise?.product?.id) === addProductId;
      }
      return getVariantNumericId(line?.merchandise?.id) === addVariantId;
    }) || null;
  }

  function findMergeableMainLine(cart, addLine, shouldHaveGift) {
    return findMainLineByGiftState(cart, addLine, shouldHaveGift);
  }

  function findMergeableGiftLine(cart, addLine) {
    if (!cart || !isGiftAddLine(addLine) || addLine.sellingPlanId) return null;

    return getCartLines(cart).find((line) => {
      if (line?.merchandise?.id !== addLine.merchandiseId) return false;
      return isDiscountedFreeLine(line);
    }) || null;
  }

  function cloneAddLineWithQuantity(line, quantity) {
    const nextLine = {
      ...line,
      quantity: Math.max(Number.parseInt(quantity || '1', 10) || 1, 1)
    };

    return nextLine;
  }

  function clonePlainAddLine(line, quantity) {
    const plainLine = {
      merchandiseId: line.merchandiseId,
      quantity: Math.max(Number.parseInt(quantity || line.quantity || '1', 10) || 1, 1)
    };

    if (line.sellingPlanId) plainLine.sellingPlanId = line.sellingPlanId;
    return plainLine;
  }

  function getGiftGroup(line) {
    return String(line?._giftGroup || '');
  }

  function stripLineMeta(line) {
    const cleanLine = { ...line };
    delete cleanLine.attributes;
    delete cleanLine._giftLine;
    delete cleanLine._giftMainLine;
    delete cleanLine._giftGroup;
    delete cleanLine._giftCoupon;
    delete cleanLine._giftParentVariantId;
    delete cleanLine._giftParentProductId;
    return cleanLine;
  }

  function stripLinesMeta(lines) {
    return lines.map(stripLineMeta);
  }

  function queueMainLine(line, cart, updatesByLineId, pendingByVariantId, linesToAdd, shouldHaveGift) {
    const existingLine = findMergeableMainLine(cart, line, shouldHaveGift);
    if (existingLine) {
      const currentUpdate = updatesByLineId.get(existingLine.id);
      const baseQuantity = currentUpdate?.quantity || Number.parseInt(existingLine.quantity || '0', 10) || 0;
      updatesByLineId.set(existingLine.id, {
        id: existingLine.id,
        quantity: baseQuantity + (Number.parseInt(line.quantity || '1', 10) || 1)
      });
      return true;
    }

    if (!line.sellingPlanId && shouldHaveGift === false) {
      const pendingLine = pendingByVariantId.get(line.merchandiseId);
      if (pendingLine) {
        pendingLine.quantity += Number.parseInt(line.quantity || '1', 10) || 1;
        return false;
      }

      pendingByVariantId.set(line.merchandiseId, line);
    }

    linesToAdd.push(line);
    return false;
  }

  function buildAddLineMergePlan(lines, cart) {
    const updatesByLineId = new Map();
    const addLinesByVariantId = new Map();
    const addGiftKeys = new Set();
    const convertedGiftGroups = new Set();
    const linesToAdd = [];

    lines.forEach((line) => {
      const giftGroup = getGiftGroup(line);
      const lineHasGift = Boolean(giftGroup && lines.some((candidate) => {
        return isGiftAddLine(candidate) && getGiftGroup(candidate) === giftGroup;
      }));

      if (!isGiftAddLine(line) && lineHasGift) {
        const hasGiftParentLine = Boolean(findMergeableMainLine(cart, line, true));
        if (hasGiftParentLine) {
          queueMainLine(clonePlainAddLine(line), cart, updatesByLineId, addLinesByVariantId, linesToAdd, false);
          convertedGiftGroups.add(giftGroup);
          return;
        }

        const requestedQuantity = Math.max(Number.parseInt(line.quantity || '1', 10) || 1, 1);
        queueMainLine(cloneAddLineWithQuantity(line, 1), cart, updatesByLineId, addLinesByVariantId, linesToAdd, true);

        if (requestedQuantity > 1) {
          queueMainLine(clonePlainAddLine(line, requestedQuantity - 1), cart, updatesByLineId, addLinesByVariantId, linesToAdd, false);
        }

        return;
      }

      if (isGiftAddLine(line)) {
        if (convertedGiftGroups.has(getGiftGroup(line))) {
          queueMainLine(clonePlainAddLine(line), cart, updatesByLineId, addLinesByVariantId, linesToAdd, false);
          return;
        }

        const existingGiftLine = findMergeableGiftLine(cart, line);
        const giftKey = [
          line.merchandiseId,
          line._giftParentVariantId || '',
          line._giftParentProductId || '',
          line._giftCoupon || ''
        ].join('|');

        if (existingGiftLine || addGiftKeys.has(giftKey)) {
          return;
        }

        addGiftKeys.add(giftKey);
        linesToAdd.push(line);
        return;
      }

      queueMainLine(clonePlainAddLine(line), cart, updatesByLineId, addLinesByVariantId, linesToAdd, false);
    });

    return {
      linesToAdd,
      linesToUpdate: Array.from(updatesByLineId.values())
    };
  }

  function getLineQuantityLimits(cart, line) {
    if (!line?.merchandise) return { min: 1, max: Infinity };

    const available = getAvailableQuantity(line.merchandise.quantityAvailable);
    if (available !== null) {
      const otherQuantity = getCartVariantQuantity(cart, line.merchandise.id, line.id);
      return {
        min: 1,
        max: Math.max(1, available - otherQuantity)
      };
    }

    if (line.merchandise.availableForSale === false) {
      return { min: 1, max: Number.parseInt(line.quantity || '0', 10) || 1 };
    }

    return { min: 1, max: Infinity };
  }

  function getLineRemainingQuantity(cart, line) {
    const limits = getLineQuantityLimits(cart, line);
    if (!Number.isFinite(limits.max)) return Infinity;
    return Math.max(0, limits.max - (Number.parseInt(line?.quantity || '0', 10) || 0));
  }

  function limitAddLinesByAvailability(lines, cart) {
    const normalizedLines = toArray(lines).map(normalizeAddLine);
    if (!normalizedLines.length) return [];

    const plannedQuantities = new Map();
    const skippedMainGiftGroups = new Set();
    let primaryLineSkipped = false;
    const preparedLines = [];

    normalizedLines.forEach((line, index) => {
      const requestedQuantity = Number.parseInt(line.quantity || '1', 10) || 1;
      const cartQuantity = getCartVariantQuantity(cart, line.merchandiseId);
      const plannedQuantity = plannedQuantities.get(line.merchandiseId) || 0;
      const available = getVariantAvailabilityFromCart(cart, line.merchandiseId);
      const giftGroup = getGiftGroup(line);
      const giftAddLine = isGiftAddLine(line);

      if (available !== null) {
        const remaining = available - cartQuantity - plannedQuantity;
        if (remaining <= 0) {
          if (index === 0 && !giftAddLine) primaryLineSkipped = true;
          if (giftGroup && !giftAddLine) skippedMainGiftGroups.add(giftGroup);
          return;
        }

        line.quantity = Math.min(requestedQuantity, remaining);
      }

      plannedQuantities.set(line.merchandiseId, plannedQuantity + line.quantity);
      preparedLines.push(line);
    });

    if (primaryLineSkipped && normalizedLines.length > 1) return [];

    return preparedLines.filter((line) => {
      const giftGroup = getGiftGroup(line);
      return !(giftGroup && isGiftAddLine(line) && skippedMainGiftGroups.has(giftGroup));
    });
  }

  function dispatchCartEvent(type, detail) {
    if (typeof document === 'undefined') return;

    document.dispatchEvent(new CustomEvent(`storefront-cart:${type}`, {
      detail: detail || {}
    }));
  }

  async function graphql(query, variables, options) {
    const accessToken = resolveAccessToken();
    if (!accessToken) {
      throw new StorefrontCartError('Storefront API access token is missing.');
    }
    const requestVariables = variables || {};

    const response = await fetch(getGraphqlEndpoint(), {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': accessToken
      },
      body: JSON.stringify({
        query,
        variables: requestVariables
      }),
      signal: options?.signal
    });

    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      const message = payload?.errors ? formatApiErrors(payload.errors) : `Storefront API request failed with status ${response.status}.`;
      throw new StorefrontCartError(message, { response, payload });
    }

    if (payload?.errors?.length) {
      throw new StorefrontCartError(formatApiErrors(payload.errors), { graphQLErrors: payload.errors, payload });
    }

    return payload?.data || {};
  }

  async function createCart(input, options) {
    try {
      const cartInput = await applyCartCurrencyToInput(normalizeCartInput(input), options);
      const data = await graphql(CART_CREATE_MUTATION, {
        input: cartInput
      }, options);
      const cart = getMutationPayload(data, 'cartCreate');
      dispatchCartEvent('created', { cart });
      dispatchCartEvent('updated', { cart });
      return cart;
    }
    catch (error) {
      dispatchCartEvent('error', { error });
      throw error;
    }
  }

  async function getCart(cartId, options) {
    const id = cartId || readStoredCartId();
    if (!id) return null;

    const data = await graphql(CART_QUERY, { cartId: id }, options);
    const cart = data.cart || null;

    if (!cart) {
      clearStoredCartId();
      return null;
    }

    return persistCart(await synchronizeCartCurrency(cart, options));
  }

  async function ensureCart(options) {
    const config = options || {};
    const cartId = config.cartId || readStoredCartId();

    if (config.forceCreate || !cartId) {
      return createCart(config.input || config.createInput || {}, config);
    }

    const cart = await getCart(cartId, config);
    if (cart) return cart;

    return createCart(config.input || config.createInput || {}, config);
  }

  async function ensureCartId(options) {
    const cartId = options?.cartId || readStoredCartId();
    if (cartId) return cartId;

    const cart = await createCart(options?.createInput || {}, options);
    return cart.id;
  }

  async function addLines(lines, options) {
    try {
      const existingCart = options?.cart || await getCart(options?.cartId, options).catch(() => null);
      const normalizedLines = limitAddLinesByAvailability(lines, existingCart);
      if (!normalizedLines.length) {
        throw new StorefrontCartError(MAX_QUANTITY_ERROR_MESSAGE);
      }

      const cartId = await ensureCartId(options);
      const mergePlan = buildAddLineMergePlan(normalizedLines, existingCart);
      let cart = null;

      if (mergePlan.linesToUpdate.length) {
        const data = await graphql(CART_LINES_UPDATE_MUTATION, {
          cartId,
          lines: stripLinesMeta(mergePlan.linesToUpdate)
        }, options);
        cart = getMutationPayload(data, 'cartLinesUpdate');
      }

      if (mergePlan.linesToAdd.length) {
        const data = await graphql(CART_LINES_ADD_MUTATION, {
          cartId,
          lines: stripLinesMeta(mergePlan.linesToAdd)
        }, options);
        cart = getMutationPayload(data, 'cartLinesAdd');
      }

      dispatchCartEvent('updated', {
        cart,
        action: 'addLines',
        lines: normalizedLines,
        addedLines: mergePlan.linesToAdd,
        updatedLines: mergePlan.linesToUpdate
      });
      return cart;
    }
    catch (error) {
      dispatchCartEvent('error', { error });
      throw error;
    }
  }

  async function addItem(variantId, quantity, options) {
    const desiredQuantity = quantity === undefined ? 1 : toPositiveInteger(quantity, 1) || 1;
    const cart = options?.cart || await ensureCart(options);
    const line = findLineByVariantId(cart, variantId, options?.matchLine);

    if (line) {
      const remaining = getLineRemainingQuantity(cart, line);
      if (remaining <= 0) {
        throw new StorefrontCartError(MAX_QUANTITY_ERROR_MESSAGE);
      }

      return addLines({
        merchandiseId: variantId,
        quantity: Math.min(desiredQuantity, remaining),
        sellingPlanId: options?.sellingPlanId
      }, options);
    }

    return addLines({
      merchandiseId: variantId,
      quantity: desiredQuantity,
      sellingPlanId: options?.sellingPlanId
    }, options);
  }

  async function updateLines(lines, options) {
    try {
      const normalizedLines = toArray(lines).map(normalizeUpdateLine);
      if (!normalizedLines.length) {
        throw new StorefrontCartError('At least one cart line update is required.');
      }

      const removeIds = normalizedLines
        .filter((line) => line.quantity !== undefined && line.quantity < 1)
        .map((line) => line.id);
      const updates = normalizedLines.filter((line) => line.quantity === undefined || line.quantity > 0);

      let cart = null;
      if (updates.length) {
        const data = await graphql(CART_LINES_UPDATE_MUTATION, {
          cartId: await ensureCartId(options),
          lines: updates
        }, options);
        cart = getMutationPayload(data, 'cartLinesUpdate');
      }

      if (removeIds.length) {
        cart = await removeLines(removeIds, options);
      }

      if (updates.length) {
        dispatchCartEvent('updated', { cart, action: 'updateLines', lines: normalizedLines });
      }

      return cart;
    }
    catch (error) {
      dispatchCartEvent('error', { error });
      throw error;
    }
  }

  function updateLine(lineId, line, options) {
    return updateLines({
      ...(line || {}),
      id: lineId
    }, options);
  }

  async function setLineQuantity(lineId, quantity, options) {
    const cart = options?.cart || await getCart(options?.cartId, options);
    const line = findLine(cart, lineId);

    if (!line) {
      throw new StorefrontCartError('Cart line was not found.', { lineId, cart });
    }

    const nextQuantity = Math.max(toPositiveInteger(quantity, 1) || 1, 1);
    const limits = getLineQuantityLimits(cart, line);

    if (nextQuantity > limits.max) {
      if (Number(line.quantity || 0) >= limits.max) {
        throw new StorefrontCartError(MAX_QUANTITY_ERROR_MESSAGE);
      }
      return updateLine(lineId, { quantity: limits.max }, { ...options, cart });
    }

    return updateLine(lineId, { quantity: nextQuantity }, { ...options, cart });
  }

  async function incrementLine(lineId, step, options) {
    const delta = Number.parseInt(step === undefined ? 1 : step, 10);
    const cart = options?.cart || await getCart(options?.cartId, options);
    const line = findLine(cart, lineId);

    if (!line) {
      throw new StorefrontCartError('Cart line was not found.', { lineId, cart });
    }

    return setLineQuantity(lineId, line.quantity + (Number.isNaN(delta) ? 1 : delta), options);
  }

  function decrementLine(lineId, step, options) {
    const delta = Number.parseInt(step === undefined ? 1 : step, 10);
    return incrementLine(lineId, -Math.abs(Number.isNaN(delta) ? 1 : delta), options);
  }

  async function removeLines(lineIds, options) {
    try {
      const ids = toArray(lineIds).map(String).filter(Boolean);
      if (!ids.length) {
        throw new StorefrontCartError('At least one cart line ID is required.');
      }

      const data = await graphql(CART_LINES_REMOVE_MUTATION, {
        cartId: await ensureCartId(options),
        lineIds: ids
      }, options);
      const cart = getMutationPayload(data, 'cartLinesRemove');
      dispatchCartEvent('updated', { cart, action: 'removeLines', lineIds: ids });
      return cart;
    }
    catch (error) {
      dispatchCartEvent('error', { error });
      throw error;
    }
  }

  function removeLine(lineId, options) {
    return removeLines([lineId], options);
  }

  async function clearCart(options) {
    const cart = options?.cart || await getCart(options?.cartId, options);
    const lineIds = getCartLines(cart).map((line) => line.id);

    if (!lineIds.length) return cart;
    return removeLines(lineIds, options);
  }

  async function setVariantQuantity(variantId, quantity, options) {
    const cart = options?.cart || await ensureCart(options);
    const line = findLineByVariantId(cart, variantId, options?.matchLine);
    const nextQuantity = toPositiveInteger(quantity, 0);

    if (line) return setLineQuantity(line.id, nextQuantity, { ...options, cart });
    if (nextQuantity < 1) return cart;

    return addItem(variantId, nextQuantity, options);
  }

  async function incrementVariant(variantId, step, options) {
    const delta = Number.parseInt(step === undefined ? 1 : step, 10);
    const cart = options?.cart || await ensureCart(options);
    const line = findLineByVariantId(cart, variantId, options?.matchLine);
    const nextDelta = Number.isNaN(delta) ? 1 : delta;

    if (!line && nextDelta > 0) return addItem(variantId, nextDelta, options);
    if (!line) return cart;

    return setLineQuantity(line.id, line.quantity + nextDelta, { ...options, cart });
  }

  function decrementVariant(variantId, step, options) {
    const delta = Number.parseInt(step === undefined ? 1 : step, 10);
    return incrementVariant(variantId, -Math.abs(Number.isNaN(delta) ? 1 : delta), options);
  }

  async function removeVariant(variantId, options) {
    const cart = options?.cart || await getCart(options?.cartId, options);
    const line = findLineByVariantId(cart, variantId, options?.matchLine);

    if (!line) return cart;
    return removeLine(line.id, { ...options, cart });
  }

  async function updateDiscountCodes(discountCodes, options) {
    const codes = toArray(discountCodes).map(String).filter(Boolean);
    const data = await graphql(CART_DISCOUNT_CODES_UPDATE_MUTATION, {
      cartId: await ensureCartId(options),
      discountCodes: codes
    }, options);
    const cart = getMutationPayload(data, 'cartDiscountCodesUpdate');
    dispatchCartEvent('updated', { cart, action: 'updateDiscountCodes', discountCodes: codes });
    return cart;
  }

  async function updateAttributes(attributes, options) {
    const normalizedAttributes = normalizeAttributes(attributes) || [];
    const data = await graphql(CART_ATTRIBUTES_UPDATE_MUTATION, {
      cartId: await ensureCartId(options),
      attributes: normalizedAttributes
    }, options);
    const cart = getMutationPayload(data, 'cartAttributesUpdate');
    dispatchCartEvent('updated', { cart, action: 'updateAttributes', attributes: normalizedAttributes });
    return cart;
  }

  async function updateNote(note, options) {
    const data = await graphql(CART_NOTE_UPDATE_MUTATION, {
      cartId: await ensureCartId(options),
      note: String(note || '')
    }, options);
    const cart = getMutationPayload(data, 'cartNoteUpdate');
    dispatchCartEvent('updated', { cart, action: 'updateNote', note });
    return cart;
  }

  async function updateBuyerIdentity(buyerIdentity, options) {
    const data = await graphql(CART_BUYER_IDENTITY_UPDATE_MUTATION, {
      cartId: await ensureCartId(options),
      buyerIdentity: buyerIdentity || {}
    }, options);
    const cart = getMutationPayload(data, 'cartBuyerIdentityUpdate');
    dispatchCartEvent('updated', { cart, action: 'updateBuyerIdentity', buyerIdentity });
    return cart;
  }

  async function getCheckoutUrl(options) {
    const cart = await ensureCart(options);
    return cart.checkoutUrl;
  }

  function configure(options) {
    Object.assign(settings, options || {});
    return { ...settings };
  }

  const api = {
    configure,
    graphql,
    createCart,
    getCart,
    ensureCart,
    addItem,
    addLines,
    updateLine,
    updateLines,
    setLineQuantity,
    incrementLine,
    decrementLine,
    removeLine,
    removeLines,
    clearCart,
    setVariantQuantity,
    incrementVariant,
    decrementVariant,
    removeVariant,
    updateDiscountCodes,
    updateAttributes,
    updateNote,
    updateBuyerIdentity,
    getCheckoutUrl,
    getCartId: readStoredCartId,
    getCartCurrency: readCartCurrency,
    setCartId: writeStoredCartId,
    clearCartId: clearStoredCartId,
    getCartLines,
    findLine,
    findLineByVariantId,
    toShopifyGid,
    StorefrontCartError
  };

  global.AbokStorefrontCart = api;
  global.StorefrontCart = global.StorefrontCart || api;
})(typeof window !== 'undefined' ? window : globalThis);

(function (global) {
  'use strict';

  if (typeof document === 'undefined' || !global.AbokStorefrontCart) return;

  const api = global.AbokStorefrontCart;
  const DEFAULT_ACCESS_TOKEN = 'b5f5879c29bf9a8de4b6efdbe53fcabb';
  const MAX_QUANTITY_ERROR_MESSAGE = 'The maximum quantity of this item is already in your cart.';
  const loadingButtons = new WeakSet();
  const lineLocks = new Set();
  const debouncedTimers = new Map();

  api.configure({
    accessToken: DEFAULT_ACCESS_TOKEN,
    shopDomain: global.Shopify?.shop || global.location?.hostname || '',
    apiVersion: '2026-04'
  });

  function escapeHTML(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function getLines(cart) {
    return api.getCartLines(cart);
  }

  function getAvailableQuantity(value) {
    const quantity = Number.parseInt(value, 10);
    return Number.isFinite(quantity) ? quantity : null;
  }

  function getCartVariantQuantity(cart, variantId, excludeLineId) {
    let merchandiseId = '';
    try {
      merchandiseId = api.toShopifyGid(variantId, 'ProductVariant');
    }
    catch (error) {
      return 0;
    }

    return getLines(cart).reduce((total, line) => {
      if (excludeLineId && line.id === excludeLineId) return total;
      if (line?.merchandise?.id !== merchandiseId) return total;
      return total + (Number.parseInt(line.quantity || '0', 10) || 0);
    }, 0);
  }

  function getLineQuantityMax(cart, line) {
    const available = getAvailableQuantity(line?.merchandise?.quantityAvailable);
    if (available !== null) {
      const otherQuantity = getCartVariantQuantity(cart, line.merchandise.id, line.id);
      return Math.max(1, available - otherQuantity);
    }

    if (line?.merchandise?.availableForSale === false) {
      return Number.parseInt(line.quantity || '0', 10) || 1;
    }

    return Infinity;
  }

  function getLineQuantityRemaining(cart, line) {
    const maxQuantity = getLineQuantityMax(cart, line);
    if (!Number.isFinite(maxQuantity)) return Infinity;
    return Math.max(0, maxQuantity - (Number.parseInt(line?.quantity || '0', 10) || 0));
  }

  function isVariantAtMaxQuantity(cart, line) {
    const available = getAvailableQuantity(line?.merchandise?.quantityAvailable);
    if (available === null) return false;

    return getCartVariantQuantity(cart, line?.merchandise?.id) >= available;
  }

  function showMaxQuantityError(container) {
    showError(getMaxQuantityErrorMessage(), container);
  }

  function getMaxQuantityErrorMessage() {
    const message = global.theme?.cartStrings?.maxQuantityReached;
    if (message && !String(message).toLowerCase().includes('translation missing')) return message;
    return MAX_QUANTITY_ERROR_MESSAGE;
  }

  function normalizeErrorMessage(message) {
    if (message === MAX_QUANTITY_ERROR_MESSAGE) return getMaxQuantityErrorMessage();
    return message;
  }

  function formatMoney(amount, currencyCode) {
    const number = Number.parseFloat(amount || 0);
    const currency = currencyCode || 'EUR';

    try {
      return new Intl.NumberFormat(global.Shopify?.locale || undefined, {
        style: 'currency',
        currency
      }).format(number);
    }
    catch (error) {
      return `${currency} ${number.toFixed(2)}`;
    }
  }

  function getCartCurrency(cart) {
    return cart?.cost?.totalAmount?.currencyCode
      || cart?.cost?.subtotalAmount?.currencyCode
      || api.getCartCurrency()
      || global.Shopify?.currency?.active
      || 'EUR';
  }

  function getEmptyCart() {
    const currencyCode = api.getCartCurrency() || global.Shopify?.currency?.active || 'EUR';

    return {
      id: '',
      checkoutUrl: '',
      totalQuantity: 0,
      discountCodes: [],
      lines: { edges: [] },
      cost: {
        subtotalAmount: { amount: '0', currencyCode },
        totalAmount: { amount: '0', currencyCode }
      }
    };
  }

  function getVariantNumericId(variantGid) {
    return String(variantGid || '').split('/').pop();
  }

  function getProductNumericId(productGid) {
    return String(productGid || '').split('/').pop();
  }

  function findQuantityInput(form) {
    const formId = form?.getAttribute('id');
    return form?.querySelector('[name="quantity"]')
      || (formId ? document.querySelector(`[name="quantity"][form="${CSS.escape(formId)}"]`) : null);
  }

  function getFormValue(form, name) {
    if (!form) return '';
    const data = new FormData(form);
    return data.get(name)
      || form.querySelector(`[name="${CSS.escape(name)}"]`)?.value
      || '';
  }

  function getQuantity(form, fallback) {
    const input = findQuantityInput(form);
    const quantity = Number.parseInt(input?.value || getFormValue(form, 'quantity') || fallback || '1', 10);
    return Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;
  }

  function getPropertyAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData.entries()) {
      const match = /^properties\[(.+)]$/.exec(name);
      if (!match || value === '') continue;
      attributes.push({ key: match[1], value: String(value) });
    }

    return attributes;
  }

  function createGiftGroupId(variantId) {
    return ['gift', variantId || 'variant', Date.now(), Math.random().toString(36).slice(2, 8)].join('-');
  }

  function getBundleLines(form) {
    if (typeof bundlePickerManager !== 'undefined' && bundlePickerManager) {
      return bundlePickerManager.getSelectedBundles().map((bundle) => ({
        merchandiseId: bundle.variantId || bundle.productId,
        quantity: Number.parseInt(bundle.quantity || '1', 10) || 1
      })).filter((line) => line.merchandiseId);
    }

    const formId = form?.getAttribute('id');
    const selector = formId
      ? `[form="${CSS.escape(formId)}"] input[name="bundles"]:checked`
      : 'input[name="bundles"]:checked';

    return Array.from(document.querySelectorAll(selector)).map((input) => ({
      merchandiseId: input.value,
      quantity: 1
    })).filter((line) => line.merchandiseId);
  }

  function getFreeGiftLines(productId, mainVariantId, giftGroupId) {
    const coupons = [];
    const lines = Array.from(document.querySelectorAll('.free-gift-item.checked .free-gift-radio')).map((input) => {
      const giftVariantId = input.dataset.id;
      const giftProductId = input.dataset.productId;
      const coupon = input.dataset.coupon || '';

      if (!giftVariantId || (productId && giftProductId && String(giftProductId) !== String(productId))) {
        return null;
      }

      if (coupon) coupons.push(coupon);

      return {
        merchandiseId: giftVariantId,
        quantity: 1,
        _giftLine: true,
        _giftCoupon: coupon,
        _giftParentVariantId: String(mainVariantId || ''),
        _giftParentProductId: String(productId || ''),
        _giftGroup: giftGroupId || ''
      };
    }).filter(Boolean);

    return { lines, coupons };
  }

  function getButtonLines(button) {
    const variantId = button.dataset.variantId || button.getAttribute('data-variant-id');
    const productId = button.dataset.productId || button.getAttribute('data-product-id') || getFormValue(button.closest('form'), 'product-id');
    const quantity = Number.parseInt(button.dataset.quantity || button.getAttribute('data-quantity') || '1', 10) || 1;
    const giftVariantId = button.dataset.freeGiftVariantId || button.getAttribute('data-free-gift-variant-id');
    const giftCoupon = button.dataset.freeGiftCoupon || button.getAttribute('data-free-gift-coupon') || '';
    const lines = [];
    const coupons = [];
    const hasGift = Boolean(giftVariantId);
    const giftGroupId = hasGift ? createGiftGroupId(variantId) : '';

    if (variantId) {
      const mainLine = {
        merchandiseId: variantId,
        quantity
      };

      if (hasGift) {
        mainLine._giftMainLine = true;
        mainLine._giftCoupon = giftCoupon;
        mainLine._giftParentProductId = String(productId || '');
        mainLine._giftGroup = giftGroupId;
      }

      lines.push(mainLine);
    }

    if (giftVariantId) {
      lines.push({
        merchandiseId: giftVariantId,
        quantity: 1,
        _giftLine: true,
        _giftCoupon: giftCoupon,
        _giftParentVariantId: String(variantId || ''),
        _giftParentProductId: String(productId || ''),
        _giftGroup: giftGroupId
      });
      if (giftCoupon) coupons.push(giftCoupon);
    }

    return { lines, coupons, variantId };
  }

  function getCheckoutLines(lines) {
    return lines.map((line) => {
      const cleanLine = {
        merchandiseId: line.merchandiseId,
        quantity: line.quantity
      };

      if (line.sellingPlanId) cleanLine.sellingPlanId = line.sellingPlanId;
      return cleanLine;
    });
  }

  function getFormLines(form) {
    const formData = new FormData(form);
    const variantId = getFormValue(form, 'id');
    const productId = getFormValue(form, 'product-id');
    const quantity = getQuantity(form);
    const sellingPlanId = getFormValue(form, 'selling_plan');
    const giftGroupId = createGiftGroupId(variantId);
    const lines = [];
    const freeGifts = getFreeGiftLines(productId, variantId, giftGroupId);

    if (variantId) {
      const mainLine = {
        merchandiseId: variantId,
        quantity
      };

      if (freeGifts.lines.length) {
        mainLine._giftMainLine = true;
        mainLine._giftCoupon = freeGifts.coupons[0] || '';
        mainLine._giftParentProductId = String(productId || '');
        mainLine._giftGroup = giftGroupId;
      }

      if (sellingPlanId) mainLine.sellingPlanId = sellingPlanId;
      lines.push(mainLine);
    }

    lines.push(...getBundleLines(form));

    lines.push(...freeGifts.lines);

    return {
      lines,
      coupons: freeGifts.coupons,
      variantId
    };
  }

  function setLoading(element, loading) {
    if (!element) return;

    if (loading) {
      element.setAttribute('aria-busy', 'true');
    }
    else {
      element.removeAttribute('aria-busy');
    }
  }

  function setLineLoading(lineId, loading) {
    if (!lineId) return;

    document.querySelectorAll(`[data-storefront-line-id="${CSS.escape(lineId)}"]`).forEach((element) => {
      const loader = element.matches('[data-storefront-line-loader]')
        ? element
        : element.querySelector('[data-storefront-line-loader]');

      if (loader) loader.hidden = !loading;
      element.querySelectorAll('[data-storefront-remove]').forEach((button) => {
        button.disabled = loading;
        button.classList.toggle('opacity-50', loading);
        button.setAttribute('aria-busy', loading ? 'true' : 'false');
      });
    });
  }

  function showError(message, container) {
    const text = normalizeErrorMessage(message) || global.theme?.cartStrings?.error || 'Cart request failed.';
    // const target = container?.querySelector?.('.product-form__error-message') || document.querySelector('.product-form__error-message');

    // if (target) {
    //   target.hidden = false;
    //   target.textContent = text;
    //   return;
    // }

    let alertContainer = document.getElementById('global-alert-container');
    if (!alertContainer) {
      alertContainer = document.createElement('div');
      alertContainer.id = 'global-alert-container';
      alertContainer.className = 'global-alert-container';
      alertContainer.style.cssText = 'position:fixed;top:20px;right:20px;z-index:9999;display:grid;gap:10px;';
      document.body.appendChild(alertContainer);
    }

    const alert = document.createElement('div');
    alert.className = 'alert alert--error';
    alert.style.cssText = 'padding:14px 16px;background:#fef2f2;color:#b91c1c;border-radius:6px;box-shadow:0 2px 8px rgba(0,0,0,.12);';
    alert.textContent = text;
    alertContainer.appendChild(alert);
    setTimeout(() => {
      alert.remove();
      if (!alertContainer.children.length) alertContainer.remove();
    }, 3500);
  }

  function buildAjaxLikeCart(cart) {
    return {
      token: cart?.id || '',
      item_count: cart?.totalQuantity || 0,
      total_price: Math.round(Number.parseFloat(cart?.cost?.totalAmount?.amount || 0) * 100),
      items: getLines(cart).map((line) => ({
        key: line.id,
        id: getVariantNumericId(line.merchandise?.id),
        quantity: line.quantity,
        product_title: line.merchandise?.product?.title || '',
        variant_title: line.merchandise?.title || '',
        price: Math.round(Number.parseFloat(line.cost?.amountPerQuantity?.amount || 0) * 100),
        final_price: Math.round(Number.parseFloat(line.cost?.amountPerQuantity?.amount || 0) * 100),
        final_line_price: Math.round(Number.parseFloat(line.cost?.totalAmount?.amount || 0) * 100)
      })),
      storefrontCart: cart,
      sections: {}
    };
  }

  function publishCartUpdate(cart, source, productVariantId) {
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
      detail: { product: buildAjaxLikeCart(cart) }
    }));

    if (global.theme?.pubsub?.publish && global.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
      global.theme.pubsub.publish(global.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        source,
        productVariantId,
        cart: buildAjaxLikeCart(cart)
      });
    }
  }

  function getCartQuantityByVariantId(cart, variantId) {
    return getLines(cart).reduce((total, line) => {
      if (line?.merchandise?.id !== variantId) return total;
      return total + (Number.parseInt(line.quantity || '0', 10) || 0);
    }, 0);
  }

  function getActualAddedQuantity(previousCart, cart, lines) {
    const variantIds = Array.from(new Set(lines.map((line) => {
      try {
        return api.toShopifyGid(line.merchandiseId || line.variantId || line.id, 'ProductVariant');
      }
      catch (error) {
        return '';
      }
    }).filter(Boolean)));

    const addedQuantity = variantIds.reduce((total, variantId) => {
      return total + Math.max(0, getCartQuantityByVariantId(cart, variantId) - getCartQuantityByVariantId(previousCart, variantId));
    }, 0);

    return addedQuantity || lines.reduce((total, line) => total + (Number.parseInt(line.quantity || '1', 10) || 1), 0);
  }

  function getDiscountAllocationInfo(cart) {
    return getLines(cart).flatMap((line) => {
      return (line.discountAllocations || []).map((discount) => ({
        variant_id: getVariantNumericId(line.merchandise?.id),
        code: discount.code || '',
        title: discount.title || '',
        discounted_amount: discount.discountedAmount?.amount || '',
        currency: discount.discountedAmount?.currencyCode || ''
      }));
    });
  }

  function getCartCouponReportInfo(cart) {
    return {
      discount_codes: getApplicableDiscountCodes(cart),
      discount_allocations: getDiscountAllocationInfo(cart)
    };
  }

  function getLineReportInfo(line, quantityOverride) {
    if (!line) return null;

    return {
      product_name: line.merchandise?.product?.title || '',
      product_id: getProductNumericId(line.merchandise?.product?.id),
      variant_id: getVariantNumericId(line.merchandise?.id),
      variant_name: line.merchandise?.title || '',
      quantity: typeof quantityOverride === 'number'
        ? quantityOverride
        : Number.parseInt(line.quantity || '0', 10) || 0,
      price: Number.parseFloat(line.cost?.amountPerQuantity?.amount || line.merchandise?.price?.amount || '0') || 0
    };
  }

  function getLineReportSnapshot(target, quantity) {
    const lineItem = target?.closest?.('[data-cart-line-item]');
    if (!lineItem) return null;

    return {
      product_name: lineItem.dataset.productTitle || '',
      product_id: lineItem.dataset.productId || '',
      variant_id: lineItem.dataset.variantId || '',
      variant_name: lineItem.dataset.variantTitle || '',
      quantity: Number(quantity) || 0,
      price: Number.parseFloat(lineItem.dataset.productPrice || '0') || 0
    };
  }

  function getCartReportInfo(cart) {
    const subtotal = Number.parseFloat(cart?.cost?.subtotalAmount?.amount || cart?.cost?.totalAmount?.amount || '0') || 0;
    const total = Number.parseFloat(cart?.cost?.totalAmount?.amount || '0') || 0;
    const discountAmount = Math.max(0, subtotal - total);
    const couponInfo = getDiscountAllocationInfo(cart).map((discount) => ({
      coupon_code: discount.code || discount.title || '',
      discount_amount: Number.parseFloat(discount.discounted_amount || '0') || 0,
      currency: discount.currency || ''
    }));

    return {
      items: getLines(cart).map((line) => getLineReportInfo(line)).filter(Boolean),
      total_quantity: Number.parseInt(cart?.totalQuantity || '0', 10) || 0,
      original_price: subtotal,
      discount_price: discountAmount,
      discount_amount: discountAmount,
      total_price: total,
      coupon_info: couponInfo,
      coupons: (cart?.discountCodes || []).map((discount) => ({
        code: discount.code || '',
        applicable: discount.applicable
      })),
      coupon_codes: Array.from(new Set(couponInfo.map((coupon) => coupon.coupon_code).filter(Boolean)))
    };
  }

  function getCartActionReportElement(target, name, quantity) {
    if (!target) return null;

    const quantityInput = target.closest?.('quantity-input');
    if (quantityInput) {
      return quantityInput.querySelector(`[data-cart-action-report][data-cart-action="${name}"]`);
    }

    if (quantity === 0) {
      return target.closest?.('[data-cart-action-report][data-cart-action="remove"]') || target.closest?.('[data-cart-action-report]');
    }

    return target.closest?.('[data-cart-action-report]');
  }

  function sendExposeAction(reportElement, action) {
    if (!reportElement || !global.Expose || typeof global.Expose.reportAction !== 'function') return;

    const options = { keepalive: true };
    const payload = typeof global.Expose.buildExposureAction === 'function'
      ? global.Expose.buildExposureAction(action, reportElement, options)
      : Promise.resolve(action);

    payload
      .then((data) => global.Expose.reportAction(data, options))
      .catch((error) => console.warn('Cart action report failed:', error));
  }

  function reportCartAction(trigger, previousLine, quantity, name, cart) {
    const reportElement = getCartActionReportElement(trigger, name, quantity);
    if (!reportElement) return;

    const updatedLine = getLines(cart).find((line) => line.id === previousLine?.id);
    const itemInfo = getLineReportInfo(updatedLine || previousLine, quantity) || getLineReportSnapshot(trigger, quantity);
    const action = {
      action_type: reportElement.dataset.exposeActionType || 'click',
      item_id: reportElement.dataset.exposeItemId || getVariantNumericId(previousLine?.merchandise?.id),
      domain: reportElement.dataset.domain,
      exinfo1: JSON.stringify({
        source: new URLSearchParams(global.location.search).get('source') || '',
        url: global.location.href,
        item_info: itemInfo,
        cart_info: getCartReportInfo(cart)
      })
    };

    sendExposeAction(reportElement, action);
  }

  function reportAddToCart(trigger, cart, variantId, lines, previousCart) {
    const reportElement = trigger?.closest?.('[data-add-to-cart-report], expose-click-element, expose-element')
      || document.querySelector('[data-add-to-cart-report]');

    if (!reportElement) return;

    const itemId = lines.map((line) => line.merchandiseId || line.variantId || line.id).filter(Boolean).join('_')
      || reportElement.dataset.exposeItemId
      || variantId
      || '';
    const firstLine = getLines(cart).find((line) => getVariantNumericId(line.merchandise?.id) === String(variantId)) || getLines(cart)[0];
    const action = {
      action_type: reportElement.dataset.exposeActionType || 'click',
      item_id: itemId,
      domain: reportElement.dataset.domain,
      exinfo1: JSON.stringify({
        source: new URLSearchParams(global.location.search).get('source') || '',
        url: global.location.href,
        coupon_info: getCartCouponReportInfo(cart),
        cart_info: getCartReportInfo(cart),
        item_info: {
          product_name: firstLine?.merchandise?.product?.title || reportElement.dataset.productTitle || '',
          product_id: reportElement.dataset.productId || '',
          variant_id: variantId || reportElement.dataset.variantId || '',
          variant_name: firstLine?.merchandise?.title || reportElement.dataset.variantTitle || '',
          quantity: getActualAddedQuantity(previousCart, cart, lines),
          price: Number.parseFloat(firstLine?.cost?.amountPerQuantity?.amount || 0)
        }
      })
    };

    reportElement.dataset.exposeItemId = itemId;
    sendExposeAction(reportElement, action);
  }

  function reportCartRemove(trigger, cart, line) {
    reportCartAction(trigger, line, 0, 'remove', cart);
  }

  function reportCheckoutStart(reportElement, cart) {
    if (!reportElement) return Promise.resolve();

    const action = {
      action_type: reportElement.dataset.exposeActionType || 'click',
      item_id: reportElement.dataset.exposeItemId || 'cart-checkout-start',
      domain: reportElement.dataset.domain,
      exinfo1: JSON.stringify({
        source: new URLSearchParams(global.location.search).get('source') || '',
        url: global.location.href,
        cart_info: getCartReportInfo(cart)
      })
    };

    if (!global.Expose || typeof global.Expose.reportAction !== 'function') return Promise.resolve();

    const options = { keepalive: true };
    const payload = typeof global.Expose.buildExposureAction === 'function'
      ? global.Expose.buildExposureAction(action, reportElement, options)
      : Promise.resolve(action);

    return payload.then((data) => global.Expose.reportAction(data, options));
  }

  function getLineAttributes(line) {
    return (line?.attributes || []).reduce((attributes, attribute) => {
      if (attribute?.key) attributes[attribute.key] = attribute.value || '';
      return attributes;
    }, {});
  }

  function getLineDiscountCodes(line) {
    return (line?.discountAllocations || [])
      .map((discount) => String(discount?.code || '').trim())
      .filter(Boolean);
  }

  function hasZeroDiscountAllocation(line, code) {
    const targetCode = String(code || '').trim();
    if (!targetCode) return false;

    return (line?.discountAllocations || []).some((discount) => {
      const discountCode = String(discount?.code || '').trim();
      const amount = Number.parseFloat(discount?.discountedAmount?.amount || '0');
      return discountCode === targetCode && amount === 0;
    });
  }

  function isGiftMainLineForGift(mainLine, giftLine) {
    return getLineDiscountCodes(giftLine).some((code) => hasZeroDiscountAllocation(mainLine, code));
  }

  function getRelatedGiftLines(cart, line) {
    if (!line || isGiftLine(line)) return [];
    return getLines(cart).filter((cartLine) => {
      return isGiftLine(cartLine) && isGiftMainLineForGift(line, cartLine);
    });
  }

  function hasRelatedGiftLine(cart, line) {
    return getRelatedGiftLines(cart, line).length > 0;
  }

  function findPlainMainLineByVariantId(cart, variantId, excludeLineId) {
    const numericVariantId = getVariantNumericId(variantId);
    return getLines(cart).find((cartLine) => {
      if (excludeLineId && cartLine.id === excludeLineId) return false;
      if (getVariantNumericId(cartLine?.merchandise?.id) !== numericVariantId) return false;
      return !hasRelatedGiftLine(cart, cartLine) && !isGiftLine(cartLine);
    }) || null;
  }

  function getPlainLineInputFromCartLine(line, quantity) {
    const input = {
      merchandiseId: line?.merchandise?.id,
      quantity: Math.max(Number.parseInt(quantity || '1', 10) || 1, 1)
    };

    return input;
  }

  function isGiftLine(line) {
    const amount = Number.parseFloat(line?.cost?.totalAmount?.amount || '0');
    return amount === 0;
  }

  function getLineProductUrl(line) {
    const variant = line?.merchandise || {};
    const product = variant.product || {};
    const variantId = getVariantNumericId(variant.id);
    return product.handle ? "/products/" + product.handle + (variantId ? "?variant=" + variantId : '') : '#';
  }

  function getLineCompareAmount(line) {
    const variant = line?.merchandise || {};
    if (isGiftLine(line)) {
      const price = Number.parseFloat(variant.price?.amount || '0');
      return price * (Number.parseInt(line.quantity || '1', 10) || 1);
    }

    const compareAt = Number.parseFloat(variant.compareAtPrice?.amount || '0');
    if (!compareAt) return 0;
    return compareAt * (Number.parseInt(line.quantity || '1', 10) || 1);
  }

  function renderLinePrice(line, options) {
    const variant = line.merchandise || {};
    const currency = line.cost?.totalAmount?.currencyCode || variant.price?.currencyCode || options.currency;
    const total = Number.parseFloat(line.cost?.totalAmount?.amount || '0');
    const compareTotal = getLineCompareAmount(line);
    const onSale = compareTotal > total;

    return '<div class="price' + (onSale ? ' price--on-sale' : '') + ' text-sm flex flex-wrap gap-1d5">' +
        '<span class="price__regular whitespace-nowrap">' + formatMoney(total, currency) + '</span>' +
        (onSale ? '<span class="price__sale inline-flex items-center relative">' + formatMoney(compareTotal, currency) + '</span>' : '') +
      '</div>';
  }

  function renderLineOptions(line) {
    const variant = line.merchandise || {};
    const attributes = line.attributes || [];
    const optionsHtml = (variant.selectedOptions || [])
      .filter((option) => option.value && option.value !== 'Default Title')
      .map((option) => '<div><dt class="sr-only">' + escapeHTML(option.name) + ':</dt><dd>' + escapeHTML(option.value) + '</dd></div>')
      .join('');
    const attributesHtml = attributes
      .filter((attribute) => attribute?.key && attribute.value && !attribute.key.startsWith('_'))
      .map((attribute) => '<div><dt class="sr-only">' + escapeHTML(attribute.key) + ':</dt><dd>' + escapeHTML(attribute.value) + '</dd></div>')
      .join('');
    const html = optionsHtml + attributesHtml;

    return html ? '<dl class="grid gap-1 text-xs text-opacity leading-tight">' + html + '</dl>' : '';
  }

  function renderQuantityControls(line, variantId, cart, displayQuantity) {
    const maxQuantity = cart ? getLineQuantityMax(cart, line) : Infinity;
    const maxAttribute = Number.isFinite(maxQuantity)
      ? ' max="' + Number(maxQuantity) + '" data-max="' + Number(maxQuantity) + '"'
      : '';
    const value = displayQuantity === undefined ? line.quantity : displayQuantity;

    return '<div class="flex flex-col gap-3 md:gap-4">' +
        '<div class="cart-quantity__info flex items-center justify-between gap-3 md:gap-4">' +
          '<quantity-input class="cart-quantity inline-flex shrink-0 relative border rounded-md" data-storefront-cart-quantity>' +
            '<expose-click-element class="inline-flex" data-manual-report data-cart-action-report data-cart-action="minus" data-expose-item-id="' + escapeHTML(variantId) + '" data-expose-action-type="click_minus">' +
              '<button type="button" name="minus" class="quantity__button p-2" data-storefront-line-minus aria-label="minus"><span class="text-3xl flex items-center justify-center">-</span></button>' +
            '</expose-click-element>' +
            '<input class="quantity__input input text-center w-full h-full" type="number" name="updates[]" inputmode="numeric" autocomplete="off" min="1" data-min="1" step="1" value="' + escapeHTML(value) + '"' + maxAttribute + ' data-storefront-quantity-input data-storefront-line-id="' + escapeHTML(line.id) + '" data-quantity-variant-id="' + escapeHTML(variantId) + '">' +
            '<expose-click-element class="inline-flex" data-manual-report data-cart-action-report data-cart-action="plus" data-expose-item-id="' + escapeHTML(variantId) + '" data-expose-action-type="click_plus">' +
              '<button type="button" name="plus" class="quantity__button p-2" data-storefront-line-plus aria-label="plus"><span class="text-3xl flex items-center justify-center">+</span></button>' +
            '</expose-click-element>' +
          '</quantity-input>' +
        '</div>' +
      '</div>';
  }

  function renderCartItem(line, options) {
    const variant = line.merchandise || {};
    const product = variant.product || {};
    const image = variant.image || {};
    const variantId = getVariantNumericId(variant.id);
    const url = getLineProductUrl(line);
    const gift = options.isGift;
    const giftParent = options.isGiftParent;
    const title = product.title || variant.title || '';

    return '<div id="CartItem-' + escapeHTML(line.id) + '" class="cart-item flex items-center justify-between gap-4 p-5 rounded-3xl border border-slate-200 bg-white ' + (gift ? 'cart-item--gift' : '') + '" data-cart-line-item data-storefront-line-id="' + escapeHTML(line.id) + '" data-variant-id="' + escapeHTML(variantId) + '">' +
        '<div class="flex flex-row items-center gap-4">' +
          (image.url ? '<a class="cart-item__media flex justify-center items-center media relative overflow-hidden shrink-0 w-20 md:w-40 aspect-square" href="' + escapeHTML(url) + '"><img src="' + escapeHTML(image.url) + '" alt="' + escapeHTML(image.altText || title) + '" loading="lazy"></a>' : '') +
          '<div class="cart-item__details grow flex flex-col justify-between gap-3">' +
            '<div class="grid gap-2">' +
              '<div class="cart-item__title">' +
                '<a href="' + escapeHTML(url) + '" class="cart__item-title reversed-link font-medium text-sm ' + (gift ? 'md:text-sm' : 'md:text-base') + ' leading-tight">' +
                  (gift ? '<span class="free-gift-badge text-2xs md: text-xs font-semibold tracking-[.2em] inline-flex">Free Gift</span> ' : '') +
                  escapeHTML(title) + (gift ? ' <span class="font-medium">X ' + escapeHTML(line.quantity) + '</span>' : '') +
                '</a>' +
              '</div>' +
              renderLineOptions(line) +
            '</div>' +
            '<div class="grid gap-3">' + renderLinePrice(line, options) + '</div>' +
          '</div>' +
        '</div>' +
        '<div class="cart-item__actions flex flex-col items-end justify-between gap-3">' +
          (!gift ? renderQuantityControls(line, variantId, options.cart, giftParent ? 1 : undefined) : '') +
          '<div class="flex items-center justify-between gap-4">' +
            '<div class="text-xs relative">' +
              '<span class="loader inline-flex align-middle absolute absolute-center" data-storefront-line-loader data-storefront-line-id="' + escapeHTML(line.id) + '" hidden><svg class="icon icon-rotator icon-xs animate-rotator" viewBox="0 0 66 66" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg"><circle class="path" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></span>' +
              '<expose-click-element data-manual-report data-cart-action-report data-cart-action="remove" data-expose-item-id="' + escapeHTML(variantId) + '" data-expose-action-type="click_remove">' +
                '<button type="button" class="link" data-storefront-remove data-storefront-line-id="' + escapeHTML(line.id) + '">' + escapeHTML(global.theme?.cartStrings?.remove || 'Remove') + '</button>' +
              '</expose-click-element>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function getGroupedLines(cart) {
    const lines = getLines(cart);
    const groups = [];
    const usedGiftIds = new Set();
    const giftLines = lines.filter(isGiftLine);
    const mainLines = lines.filter((line) => !isGiftLine(line));

    mainLines.forEach((line) => {
      const gift = giftLines.find((giftLine) => {
        if (usedGiftIds.has(giftLine.id)) return false;
        return isGiftMainLineForGift(line, giftLine);
      });

      if (gift) usedGiftIds.add(gift.id);
      groups.push({ main: line, gift });
    });

    giftLines.forEach((giftLine) => {
      if (!usedGiftIds.has(giftLine.id)) groups.push({ main: giftLine, gift: null, mainIsGift: true });
    });

    return groups;
  }

  function renderCartItems(cart, options) {
    const currency = getCartCurrency(cart);
    return getGroupedLines(cart).map((group) => {
      if (group.mainIsGift) {
        return '<div class="cart-item-group grid gap-4 p-5 rounded-3xl border border-slate-200 bg-surface">' + renderCartItem(group.main, { currency, isGift: true, cart }) + '</div>';
      }

      return '<div class="cart-item-group grid gap-4 p-5 rounded-3xl border border-slate-200 bg-surface">' +
        renderCartItem(group.main, { currency, isGift: false, isGiftParent: Boolean(group.gift), cart }) +
        (group.gift ? renderCartItem(group.gift, { currency, isGift: true, cart }) : '') +
      '</div>';
    }).join('');
  }

  function renderDiscountCodeBadges(cart) {
    const codes = getApplicableDiscountCodes(cart);

    return '<ul class="active-facets flex flex-wrap items-center gap-2" role="list">' +
      codes.map((code) =>
        '<li class="removable-facet badge badge--highlight flex items-center justify-between gap-2 text-xs lg:text-sm leading-none" aria-label="Discount ' + escapeHTML(code) + '">' +
          '<p class="facet-title">' + escapeHTML(code) + '</p>' +
          '<button type="button" class="block relative" data-storefront-discount-remove data-discount="' + escapeHTML(code) + '" aria-label="Remove ' + escapeHTML(code) + '"><span class="btn-text" data-text>x</span></button>' +
        '</li>'
      ).join('') +
    '</ul>';
  }

  function getApplicableDiscountCodes(cart) {
    return (cart?.discountCodes || [])
      .filter((discount) => discount.applicable === true)
      .map((discount) => discount.code)
      .filter(Boolean);
  }

  function renderDiscountSummary(cart) {
    const codes = getOrderDiscountCodes(cart);
    if (!codes.length) return '';

    return '<ul class="discounts storefront-cart__discount-summary grid gap-2" role="list">' +
      codes.map((code) =>
        '<li class="discounts__discount text-sm sm:text-base flex flex-wrap items-center justify-between gap-1d5">' +
          '<p class="flex items-center gap-1d5">' +
            '<svg class="icon icon-discount icon-sm" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg"> <path stroke-linecap="round" stroke-linejoin="round" d="M3 10.349C3 11.3274 3 11.8166 3.11052 12.2769C3.20851 12.6851 3.37012 13.0753 3.58944 13.4331C3.83681 13.8368 4.18271 14.1827 4.87451 14.8745L8.47452 18.4745C10.0586 20.0586 10.8506 20.8506 11.7639 21.1474C12.5673 21.4084 13.4327 21.4084 14.2361 21.1474C15.1494 20.8506 15.9414 20.0586 17.5255 18.4745L18.4745 17.5255C20.0586 15.9414 20.8506 15.1494 21.1474 14.2361C21.4084 13.4327 21.4084 12.5673 21.1474 11.7639C20.8506 10.8506 20.0586 10.0586 18.4745 8.47452L14.8745 4.87452C14.1827 4.18271 13.8368 3.83681 13.4331 3.58944C13.0753 3.37012 12.6851 3.20851 12.2769 3.11052C11.8166 3 11.3274 3 10.349 3H9.4C7.15979 3 6.03968 3 5.18404 3.43597C4.43139 3.81947 3.81947 4.43139 3.43597 5.18404C3 6.03968 3 7.15979 3 9.4V10.349Z"></path> <path stroke-linecap="round" stroke-linejoin="round" d="M12 10C12 11.1046 11.1046 12 10 12C8.89543 12 8 11.1046 8 10C8 8.89543 8.89543 8 10 8C11.1046 8 12 8.89543 12 10Z"></path> </svg>' +
            '<span class="storefront-cart__discount-icon" aria-hidden="true">' +
            escapeHTML(code) +
            '</span>' +
          '</p>' +
          renderDiscountSavings(cart, code) +
        '</li>'
      ).join('') +
    '</ul>';
  }

  function getProductDiscountCodes(cart) {
    const giftLines = getLines(cart).filter(isGiftLine);
    return new Set(giftLines
      .flatMap((giftLine) => getLineDiscountCodes(giftLine))
      .filter((code) => getLines(cart).some((line) => !isGiftLine(line) && hasZeroDiscountAllocation(line, code))));
  }

  function getOrderDiscountCodes(cart) {
    const productCoupons = getProductDiscountCodes(cart);

    return getApplicableDiscountCodes(cart)
      .filter((code) => code && !productCoupons.has(String(code)));
  }

  function getDiscountSavingsAmount(cart) {
    const subtotal = Number.parseFloat(cart?.cost?.subtotalAmount?.amount || '0');
    const total = Number.parseFloat(cart?.cost?.totalAmount?.amount || '0');
    return Math.max(subtotal - total, 0);
  }

  function normalizeDiscountCode(code) {
    return String(code || '').trim().toLowerCase();
  }

  function getDiscountAllocationCode(discount) {
    return String(discount?.code || discount?.title || '').trim();
  }

  function getDiscountSavingsForCodes(discountAllocations, codes) {
    const normalizedCodes = new Set(Array.from(codes || []).map(normalizeDiscountCode).filter(Boolean));
    if (!normalizedCodes.size) return 0;

    return (discountAllocations || []).reduce((total, discount) => {
      if (!normalizedCodes.has(normalizeDiscountCode(getDiscountAllocationCode(discount)))) return total;
      return total + (Number.parseFloat(discount?.discountedAmount?.amount || '0') || 0);
    }, 0);
  }

  function getLineDiscountSavingsForCodes(cart, codes) {
    return getLines(cart).reduce((total, line) => {
      return total + getDiscountSavingsForCodes(line?.discountAllocations, codes);
    }, 0);
  }

  function getCartDiscountSavingsForCodes(cart, codes) {
    return getDiscountSavingsForCodes(cart?.discountAllocations, codes);
  }

  function getProductDiscountSavingsAmount(cart) {
    return getLineDiscountSavingsForCodes(cart, getProductDiscountCodes(cart));
  }

  function getOrderDiscountSavingsAmount(cart, code) {
    const orderCodes = getOrderDiscountCodes(cart);
    const codes = code ? [code] : orderCodes;
    if (!codes.length) return 0;

    const lineAllocatedAmount = getLineDiscountSavingsForCodes(cart, codes);
    if (lineAllocatedAmount > 0) return lineAllocatedAmount;

    const cartAllocatedAmount = getCartDiscountSavingsForCodes(cart, codes);
    if (cartAllocatedAmount > 0) return cartAllocatedAmount;

    if (code && orderCodes.length > 1) return 0;

    return Math.max(getDiscountSavingsAmount(cart) - getProductDiscountSavingsAmount(cart), 0);
  }

  function getCartDisplayTotalAmount(cart) {
    return cart?.cost?.totalAmount?.amount || cart?.cost?.checkoutChargeAmount?.amount || cart?.cost?.subtotalAmount?.amount || '0';
  }

  function normalizeCheckoutUrl(checkoutUrl) {
    if (!checkoutUrl) return '';

    try {
      const url = new URL(checkoutUrl, global.location.origin);
      const currentHost = global.location.hostname;
      const checkoutHost = url.hostname;

      if (currentHost && checkoutHost && checkoutHost !== currentHost && /(^|\.)abokpower\.com$/i.test(currentHost) && /(^|\.)abokpower\.com$/i.test(checkoutHost)) {
        url.protocol = global.location.protocol;
        url.hostname = currentHost;
        url.port = global.location.port;
      }

      return url.toString();
    }
    catch (error) {
      return checkoutUrl;
    }
  }

  function renderDiscountSavings(cart, code) {
    const amount = getOrderDiscountSavingsAmount(cart, code);
    if (!amount) return '';

    return '<span class="badge badge--onsale storefront-cart__discount-saving flex items-center gap-1d5 font-medium leading-none">-' + formatMoney(amount, getCartCurrency(cart)) + '</span>';
  }

  function getLineContextFromQuantityButton(button) {
    const lineItem = button?.closest?.('[data-cart-line-item]');
    const quantityInput = button?.closest?.('quantity-input');
    const input = quantityInput?.querySelector?.('[data-storefront-quantity-input]');

    return {
      lineId: lineItem?.dataset.storefrontLineId || input?.dataset.storefrontLineId || '',
      input
    };
  }

  function renderPaymentIcons() {
    const template = document.getElementById('StorefrontCartPaymentIcons');
    return template ? template.innerHTML : '';
  }

  function isMainDiscountExpanded() {
    return document.getElementById('StorefrontCartDiscountMain')?.hasAttribute('open') || false;
  }

  function renderDrawerFooterTop(cart) {
    const discountCount = getApplicableDiscountCodes(cart).length;

    return '<div class="drawer__footer-top flex">' +
        '<button type="button" class="flex gap-2d5 grow items-center justify-center" aria-controls="StorefrontCartNote" aria-expanded="false"><span class="reversed-link text-sm leading-tight">Order note</span></button>' +
        '<button type="button" class="flex gap-2d5 grow items-center justify-center" aria-controls="StorefrontCartDiscount" aria-expanded="false"><span class="reversed-link text-sm leading-tight">Discount</span>' + (discountCount ? '<span class="cart-discount__count flex items-center justify-center text-xs font-medium rounded-full">' + discountCount + '</span>' : '') + '</button>' +
        '<modal-element id="StorefrontCartNote" class="modal invisible absolute bottom-0 left-0 w-full h-full" role="dialog" hidden>' +
          '<overlay-element class="fixed-modal opacity-0 z-1 absolute top-0 left-0 w-full h-full" aria-controls="StorefrontCartNote" aria-expanded="false"></overlay-element>' +
          '<div class="modal__container flex flex-col z-3 absolute left-0 bottom-0 w-full"><div class="modal__header flex items-center justify-between relative"><span class="font-medium text-sm md:text-base">Order note</span><button type="button" class="close flex items-center justify-center absolute top-0 right-0" aria-controls="StorefrontCartNote" aria-expanded="false" aria-label="Close"><svg class="icon icon-close icon-sm" viewBox="0 0 20 20" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="M5 15L15 5M5 5L15 15"></path></svg></button></div><div class="modal__content flex flex-col overflow-hidden"><cart-note class="modal__scrollable grid gap-4"><div class="field"><textarea name="note" class="textarea is-floating" rows="3" placeholder=" ">' + escapeHTML(cart?.note || '') + '</textarea><label class="label is-floating">Order note</label></div><div class="justify-self-start"><button class="button button--primary" type="button" is="hover-button" aria-controls="StorefrontCartNote" aria-expanded="false"><span class="btn-fill" data-fill></span><span class="btn-text">Apply</span></button></div></cart-note></div></div>' +
        '</modal-element>' +
        '<modal-element id="StorefrontCartDiscount" class="modal invisible absolute bottom-0 left-0 w-full h-full" role="dialog" hidden>' +
          '<overlay-element class="fixed-modal opacity-0 z-1 absolute top-0 left-0 w-full h-full" aria-controls="StorefrontCartDiscount" aria-expanded="false"></overlay-element>' +
          '<div class="modal__container flex flex-col z-3 absolute left-0 bottom-0 w-full"><div class="modal__header flex items-center justify-between relative"><span class="font-medium text-sm md:text-base">Discount</span><button type="button" class="close flex items-center justify-center absolute top-0 right-0" aria-controls="StorefrontCartDiscount" aria-expanded="false" aria-label="Close"><svg class="icon icon-close icon-sm" viewBox="0 0 20 20" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="M5 15L15 5M5 5L15 15"></path></svg></button></div><div class="modal__content flex flex-col overflow-hidden"><form class="modal__scrollable cart-discount grid gap-4" action="/cart" method="POST" novalidate is="cart-discount">' + renderDiscountCodeBadges(cart) + '<div class="field"><input type="text" name="discount" class="input is-floating" placeholder=" "><label class="label is-floating">Discount code</label></div><div class="justify-self-start"><button class="button button--primary" type="submit" is="hover-button"><span class="btn-fill" data-fill></span><span class="btn-text">Apply</span></button></div><div class="alert alert--error alert--transparent flex gap-2d5" hidden><div class="grid gap-2 align-self-center text-sm leading-tight"></div></div></form></div></div>' +
        '</modal-element>' +
      '</div>';
  }

  function renderDrawerFooter(cart) {
    const currency = getCartCurrency(cart);
    const variantIds = getLines(cart).map((line) => getVariantNumericId(line.merchandise?.id)).filter(Boolean).join('_');

    return '<div class="drawer__footer grid w-full">' +
        renderDrawerFooterTop(cart) +
        '<div class="drawer__footer-bottom grid gap-6">' +
          '<div class="grid gap-4 sm:grid-6">' +
            renderDiscountSummary(cart) +
            '<div class="grid grid-cols-2 gap-4">' +
              '<div class="tax-note text-sm sm:text-base rte">Taxes calculated at checkout.</div>' +
              '<div class="totals grid place-self-end gap-1"><span class="totals__subtotal text-sm sm:text-base">Subtotal</span><div class="totals__subtotal-value text-lg-2xl font-bold leading-none tracking-tight">' + formatMoney(getCartDisplayTotalAmount(cart), currency) + '</div></div>' +
            '</div>' +
          '</div>' +
          '<form action="/cart" method="POST" novalidate class="drawer__footer-buttons grid grid-cols-12 gap-4">' +
            '<expose-click-element class="col-span-full" data-manual-report data-cart-checkout-report data-expose-item-id="' + escapeHTML(variantIds) + '" data-expose-action-type="click_checkout">' +
              '<button class="button button--primary icon-with-text w-full" type="submit" name="checkout" data-storefront-checkout is="hover-button"><span class="btn-fill" data-fill></span><span class="btn-text">Check out</span></button>' +
            '</expose-click-element>' +
          '</form>' +
        '</div>' +
      '</div>';
  }

  function renderCartBody(cart, options) {
    const lines = getLines(cart);

    if (!lines.length) {
      return '<div class="drawer__scrollable relative flex justify-center items-start grow shrink text-center">' +
          '<div class="drawer__empty grid gap-5 md:gap-8">' +
            '<p class="drawer__empty-text h2 heading leading-none tracking-tight">Your cart is empty</p>' +
            '<a class="button button--primary button--fixed" href="/collections/all" is="hover-link"><span class="btn-fill" data-fill></span><span class="btn-text">Continue shopping</span></a>' +
          '</div>' +
        '</div>';
    }

    return '<div class="drawer__scrollable relative flex flex-col gap-9 grow shrink">' +
        '<div class="free-shipping-bar grid gap-3 w-full"><span class="text-sm leading-tight">You are eligible for free shipping.</span><progress-bar class="overflow-hidden rounded-full" style="--progress: 100%;"></progress-bar></div>' +
        '<cart-items class="cart__items grid gap-6" data-storefront-cart-items>' + renderCartItems(cart, options) + '</cart-items>' +
      '</div>' +
      renderDrawerFooter(cart);
  }

  function renderDrawer(cart) {
    const panel = document.querySelector('[id^="MiniCart-"].drawer__panel');
    if (!panel) return;

    panel.innerHTML = '<span class="sr-only">Cart</span><div class="flex flex-col h-full">' + renderCartBody(cart, { drawer: true }) + '</div>';
  }

  function renderMainCart(cart) {
    const page = document.querySelector('.cart .page-width');
    if (!page) return;
    const discountExpanded = isMainDiscountExpanded();
    const variantIds = getLines(cart).map((line) => getVariantNumericId(line.merchandise?.id)).filter(Boolean).join('_');

    const hasItems = (cart?.totalQuantity || 0) > 0;
    const emptyState = page.querySelector('.empty-state');
    if (emptyState) emptyState.hidden = hasItems;

    document.querySelector('.cart')?.classList.toggle('is-empty', !hasItems);
    document.querySelectorAll('main-cart:not([data-storefront-rendered])').forEach((element) => {
      element.hidden = true;
    });

    let mainCart = document.querySelector('main-cart[data-storefront-rendered]');
    if (!mainCart) {
      mainCart = document.createElement('main-cart');
      mainCart.id = 'StorefrontMainCart';
      mainCart.className = 'cart__content cart__content--storefront flex flex-col lg:flex-row';
      mainCart.dataset.storefrontRendered = 'true';
      page.appendChild(mainCart);
    }

    mainCart.hidden = !hasItems;
    mainCart.innerHTML =
      '<div class="grow flex flex-col gap-8 md:gap-12">' +
        '<form class="cart__form" id="cart" data-storefront-cart-form>' +
          '<cart-items class="cart__items grid gap-6" data-storefront-cart-items>' + renderCartItems(cart, { drawer: false }) + '</cart-items>' +
        '</form>' +
      '</div>' +
      '<div class="grow-0">' +
        '<sticky-element class="flex flex-col gap-6 lg:sticky top-0">' +
          '<div class="cart__sidebar flex flex-col gap-4 md:gap-6">' +
            '<div class="storefront-cart__summary-card flex flex-col gap-4 md:gap-6">' +
              renderDiscountSummary(cart) +
              '<div class="totals flex flex-wrap items-end justify-between gap-2"><p class="totals__subtotal h2 font-medium text-base-xl md:text-2xl leading-none">Subtotal</p><p class="totals__subtotal-value font-bold text-2xl md:text-3xl leading-none">' + formatMoney(getCartDisplayTotalAmount(cart), getCartCurrency(cart)) + '</p></div>' +
              '<div class="tax-note text-opacity text-2sm rte">Taxes calculated at checkout.</div>' +
              '<details class="details" ' + (discountExpanded ? 'open ' : '') + 'is="accordion-details" id="StorefrontCartDiscountMain"><summary class="details__summary flex items-center justify-between gap-2 cursor-pointer"><p class="flex items-center gap-2d5"><span class="text-base-2xl font-medium leading-none">Discount</span>' + (getApplicableDiscountCodes(cart).length ? '<span class="cart-discount__count flex items-center justify-center text-xs font-medium rounded-full">' + getApplicableDiscountCodes(cart).length + '</span>' : '') + '</p><svg class="icon icon-chevron-down" viewBox="0 0 18 10" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="M1 1L9 9L17 1"></path></svg></summary><form class="details__content cart-discount grid gap-3 md:gap-4" action="/cart" method="POST" novalidate is="cart-discount">' + renderDiscountCodeBadges(cart) + '<div class="field"><input type="text" name="discount" class="input is-floating" placeholder=" "><label class="label is-floating">Discount code</label></div><button class="button button--primary w-full" type="submit" is="hover-button"><span class="btn-fill" data-fill></span><span class="btn-text">Apply</span></button><div class="alert alert--error alert--transparent flex gap-2d5" hidden><div class="grid gap-2 align-self-center text-sm leading-tight"></div></div></form></details>' +
              '<cart-note class="grid gap-3 cart-note"><p class="leading-tight">Add a note to your order</p><div class="field"><textarea class="textarea is-floating" rows="4" name="note" form="cart" placeholder=" ">' + escapeHTML(cart?.note || '') + '</textarea><label class="label is-floating">Order note</label></div></cart-note>' +
              '<expose-click-element data-manual-report data-cart-checkout-report data-expose-item-id="' + escapeHTML(variantIds) + '" data-expose-action-type="click_checkout">' +
                '<button class="button button--primary icon-with-text w-full" type="button" data-storefront-checkout is="hover-button"><span class="btn-fill" data-fill></span><span class="btn-text">Check out</span></button>' +
              '</expose-click-element>' +
            '</div>' +
            renderPaymentIcons() +
          '</div>' +
        '</sticky-element>' +
      '</div>';
  }

  function updateCartCount(cart) {
    document.querySelectorAll('cart-count').forEach((element) => {
      const count = cart?.totalQuantity || 0;
      element.textContent = count;
      element.hidden = count < 1;
      element.setAttribute('aria-label', `${count} items`);
    });
  }

  function renderCart(cart) {
    if (!cart) return;
    updateCartCount(cart);
    renderDrawer(cart);
    renderMainCart(cart);
  }

  async function refreshCart() {
    if (!api.getCartId()) {
      const cart = getEmptyCart();
      renderCart(cart);
      return cart;
    }

    const cart = await api.getCart();
    renderCart(cart || getEmptyCart());
    return cart;
  }

  async function addLines(lines, options) {
    const validLines = lines.filter((line) => line && line.merchandiseId);
    if (!validLines.length) throw new Error('Variant ID not found.');

    const existingCart = api.getCartId() ? await api.getCart().catch(() => null) : null;
    const existingCoupons = getApplicableDiscountCodes(existingCart);
    const cart = await api.addLines(validLines);
    const couponsToApply = Array.from(new Set([
      ...existingCoupons,
      ...(options?.coupons || [])
    ]));
    const currentCart = couponsToApply.length
      ? await api.updateDiscountCodes(couponsToApply)
      : cart;

    renderCart(currentCart);
    publishCartUpdate(currentCart, options?.source || 'storefront-cart', options?.variantId);
    reportAddToCart(options?.trigger, currentCart, options?.variantId, validLines, existingCart);
    return currentCart;
  }

  async function handleProductFormSubmit(event, form) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const submitter = event.submitter || form.querySelector('[type="submit"]');
    if (submitter && loadingButtons.has(submitter)) return;

    try {
      loadingButtons.add(submitter);
      setLoading(submitter, true);
      form.querySelector('.product-form__error-message')?.setAttribute('hidden', '');

      const data = getFormLines(form);
      const cart = await addLines(data.lines, {
        coupons: data.coupons,
        source: 'product-form',
        variantId: data.variantId,
        trigger: submitter || form
      });

      if (document.body.classList.contains('template-cart') || global.theme?.settings?.cartType === 'page') {
        renderMainCart(cart);
      }
      else {
        document.querySelector('cart-drawer')?.show?.(submitter || form);
      }
    }
    catch (error) {
      console.error('[StorefrontCart] Add to cart failed:', error);
      showError(error.message, form);
    }
    finally {
      if (submitter) {
        loadingButtons.delete(submitter);
        setLoading(submitter, false);
      }
    }
  }

  async function handleAddButton(event, button, checkout) {
    event.preventDefault();
    event.stopImmediatePropagation();
    event.__storefrontCartHandled = true;
    if (loadingButtons.has(button)) return;

    try {
      loadingButtons.add(button);
      setLoading(button, true);

      const data = getButtonLines(button);
      if (checkout && button.dataset.temporaryCheckout === 'true') {
        const previousCartId = api.getCartId();

        try {
          const cart = await api.createCart({
            lines: getCheckoutLines(data.lines),
            discountCodes: data.coupons
          });

          if (previousCartId) {
            api.setCartId(previousCartId);
          }
          else {
            api.clearCartId();
          }

          global.location.href = normalizeCheckoutUrl(cart.checkoutUrl);
          return;
        }
        finally {
          if (previousCartId) {
            api.setCartId(previousCartId);
          }
          else {
            api.clearCartId();
          }
        }
      }

      const cart = await addLines(data.lines, {
        coupons: data.coupons,
        source: checkout ? 'buy-now-button' : 'add-to-cart-button',
        variantId: data.variantId,
        trigger: button
      });

      if (checkout) {
        global.location.href = normalizeCheckoutUrl(cart.checkoutUrl);
        return;
      }

      document.querySelector('cart-drawer')?.show?.(button);
    }
    catch (error) {
      console.error('[StorefrontCart] Button add failed:', error);
      showError(error.message, button.closest('form') || button.parentElement);
    }
    finally {
      if (!checkout) {
        loadingButtons.delete(button);
        setLoading(button, false);
      }
    }
  }

  async function updateLineQuantity(lineId, quantity, trigger, actionName) {
    if (!lineId || lineLocks.has(lineId)) return;

    try {
      lineLocks.add(lineId);
      setLineLoading(lineId, true);
      const currentCart = await api.getCart();
      const line = api.findLine(currentCart, lineId);
      if (!line) return;

      const requestedQuantity = Math.max(Number.parseInt(quantity || '1', 10) || 1, 1);
      const maxQuantity = getLineQuantityMax(currentCart, line);
      const nextQuantity = Number.isFinite(maxQuantity)
        ? Math.min(requestedQuantity, maxQuantity)
        : requestedQuantity;

      if (requestedQuantity > nextQuantity) {
        showMaxQuantityError();
      }

      if (hasRelatedGiftLine(currentCart, line)) {
        let cart = currentCart;
        const plainLine = findPlainMainLineByVariantId(currentCart, line.merchandise?.id, line.id);

        if (plainLine) {
          cart = await api.setLineQuantity(plainLine.id, nextQuantity, { cart: currentCart });
        }
        else {
          cart = await api.addLines([getPlainLineInputFromCartLine(line, nextQuantity)], { cart: currentCart });
        }

        const freshGiftParent = api.findLine(cart, lineId);
        if (freshGiftParent && Number.parseInt(freshGiftParent.quantity || '1', 10) !== 1) {
          cart = await api.setLineQuantity(lineId, 1, { cart });
        }

        renderCart(cart);
        publishCartUpdate(cart, 'cart-items');
        reportCartAction(trigger, line, nextQuantity, actionName || 'change', cart);
        return;
      }

      const cart = await api.setLineQuantity(lineId, nextQuantity, { cart: currentCart });
      renderCart(cart);
      publishCartUpdate(cart, 'cart-items');
      reportCartAction(trigger, line, nextQuantity, actionName || 'change', cart);
    }
    catch (error) {
      console.error('[StorefrontCart] Quantity update failed:', error);
      showError(error.message);
    }
    finally {
      setLineLoading(lineId, false);
      lineLocks.delete(lineId);
    }
  }

  async function incrementLineFromButton(lineId, trigger) {
    if (!lineId || lineLocks.has(lineId)) return;

    try {
      lineLocks.add(lineId);
      setLineLoading(lineId, true);
      const currentCart = await api.getCart();
      const line = api.findLine(currentCart, lineId);

      if (!line) return;
      if (hasRelatedGiftLine(currentCart, line)) {
        const plainLine = findPlainMainLineByVariantId(currentCart, line.merchandise?.id, line.id);

        if (plainLine) {
          const remaining = getLineQuantityRemaining(currentCart, plainLine);
          if (remaining <= 0 || isVariantAtMaxQuantity(currentCart, plainLine)) {
            showMaxQuantityError();
            return;
          }

          const cart = await api.setLineQuantity(plainLine.id, (Number.parseInt(plainLine.quantity || '0', 10) || 0) + 1, { cart: currentCart });
          renderCart(cart);
          publishCartUpdate(cart, 'cart-items');
          reportCartAction(trigger, plainLine, (Number.parseInt(plainLine.quantity || '0', 10) || 0) + 1, 'plus', cart);
          return;
        }

        const remaining = getLineQuantityRemaining(currentCart, line);
        if (remaining <= 0 || isVariantAtMaxQuantity(currentCart, line)) {
          showMaxQuantityError();
          return;
        }

        const cart = await api.addLines([getPlainLineInputFromCartLine(line, 1)], { cart: currentCart });
        renderCart(cart);
        publishCartUpdate(cart, 'cart-items');
        reportCartAction(trigger, line, 1, 'plus', cart);
        return;
      }

      const remaining = getLineQuantityRemaining(currentCart, line);
      if (remaining <= 0 || isVariantAtMaxQuantity(currentCart, line)) {
        showMaxQuantityError();
        return;
      }

      const cart = await api.setLineQuantity(lineId, (Number.parseInt(line.quantity || '0', 10) || 0) + 1, { cart: currentCart });
      renderCart(cart);
      publishCartUpdate(cart, 'cart-items');
      reportCartAction(trigger, line, (Number.parseInt(line.quantity || '0', 10) || 0) + 1, 'plus', cart);
    }
    catch (error) {
      console.error('[StorefrontCart] Quantity increment failed:', error);
      showError(error.message);
    }
    finally {
      setLineLoading(lineId, false);
      lineLocks.delete(lineId);
    }
  }

  function debounceLineQuantity(lineId, quantity, trigger) {
    clearTimeout(debouncedTimers.get(lineId));
    debouncedTimers.set(lineId, setTimeout(() => {
      debouncedTimers.delete(lineId);
      updateLineQuantity(lineId, quantity, trigger, 'change');
    }, 300));
  }

  async function removeLine(lineId, trigger) {
    if (!lineId || lineLocks.has(lineId)) return;

    try {
      lineLocks.add(lineId);
      setLineLoading(lineId, true);
      const currentCart = await api.getCart();
      const targetLine = api.findLine(currentCart, lineId);
      const targetIsGift = targetLine ? isGiftLine(targetLine) : false;
      const lineIds = [lineId];

      if (targetLine && !targetIsGift) {
        lineIds.push(...getRelatedGiftLines(currentCart, targetLine).map((line) => line.id));
      }

      const cart = await api.removeLines(Array.from(new Set(lineIds)));
      renderCart(cart);
      publishCartUpdate(cart, 'cart-remove-button');
      reportCartRemove(trigger, cart, targetLine);
    }
    catch (error) {
      console.error('[StorefrontCart] Remove line failed:', error);
      showError(error.message);
    }
    finally {
      lineLocks.delete(lineId);
      setLineLoading(lineId, false);
    }
  }

  async function redirectToCheckout(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const checkoutButton = event.target?.closest?.('[data-storefront-checkout], button[name="checkout"]');
    const checkoutReportElement = event.target?.closest?.('[data-cart-checkout-report]')
      || checkoutButton?.closest?.('[data-cart-checkout-report]');

    try {
      if (checkoutButton) setLoading(checkoutButton, true);
      const cart = await api.ensureCart();
      void reportCheckoutStart(checkoutReportElement, cart).catch((error) => {
        console.warn('Checkout start report failed:', error);
      });
      renderCart(cart);
      if (cart.checkoutUrl) global.location.href = normalizeCheckoutUrl(cart.checkoutUrl);
    }
    catch (error) {
      console.error('[StorefrontCart] Checkout failed:', error);
      showError(error.message);
      if (checkoutButton) setLoading(checkoutButton, false);
    }
  }

  async function applyDiscountForm(event, form) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const input = form.querySelector('[name="discount"]');
    const submitter = event.submitter || form.querySelector('[type="submit"]');
    const code = input?.value?.trim();
    if (!code) return;

    try {
      if (submitter) setLoading(submitter, true);
      const current = await api.ensureCart();
      const codes = new Set(getApplicableDiscountCodes(current));
      codes.add(code);
      const cart = await api.updateDiscountCodes(Array.from(codes));
      input.value = '';
      renderCart(cart);
    }
    catch (error) {
      console.error('[StorefrontCart] Discount update failed:', error);
      showError(error.message, form);
    }
    finally {
      if (submitter) setLoading(submitter, false);
    }
  }

  async function removeDiscount(code, trigger) {
    if (!code) return;

    const form = trigger?.closest?.('form[is="cart-discount"]') || document.querySelector('form[is="cart-discount"]');
    const submitter = form?.querySelector?.('[type="submit"]');

    try {
      if (submitter) setLoading(submitter, true);
      const current = await api.ensureCart();
      const nextCodes = getApplicableDiscountCodes(current)
        .filter((discountCode) => discountCode && discountCode !== code);
      const cart = await api.updateDiscountCodes(nextCodes);
      renderCart(cart);
    }
    catch (error) {
      console.error('[StorefrontCart] Discount remove failed:', error);
      showError(error.message);
    }
    finally {
      if (submitter) setLoading(submitter, false);
    }
  }

  async function applyCartNote(button) {
    const noteInput = button?.closest?.('cart-note')?.querySelector?.('textarea[name="note"]');
    if (!noteInput) return;

    try {
      setLoading(button, true);
      const cart = await api.updateNote(noteInput.value);
      renderCart(cart);
    }
    catch (error) {
      console.error('[StorefrontCart] Note update failed:', error);
      showError(error.message);
    }
    finally {
      setLoading(button, false);
    }
  }

  function defineCartDrawer() {
    if (customElements.get('cart-drawer') || typeof DrawerElement === 'undefined') return;

    customElements.define('cart-drawer', class StorefrontCartDrawer extends DrawerElement {
      get sectionId() {
        return this.getAttribute('data-section-id');
      }
    });
  }

  function defineNoopElement(name, elementClass) {
    if (!customElements.get(name)) {
      customElements.define(name, elementClass || class extends HTMLElement {});
    }
  }

  function defineButtonElement(name, checkout) {
    if (customElements.get(name)) return;

    customElements.define(name, class StorefrontCartButton extends HTMLButtonElement {
      constructor() {
        super();
        this.addEventListener('click', (event) => {
          if (event.__storefrontCartHandled) return;
          handleAddButton(event, this, checkout);
        });
      }
    }, { extends: 'button' });
  }

  function handleClick(event) {
    const target = event.target;
    const addButton = target.closest?.('button[is="add-to-cart-button"], [data-storefront-add-to-cart]');
    if (addButton) return handleAddButton(event, addButton, false);

    const buyNowButton = target.closest?.('button[is="buy-now-button"], [data-storefront-buy-now]');
    if (buyNowButton) return handleAddButton(event, buyNowButton, true);

    const checkoutButton = target.closest?.('[data-storefront-checkout], button[name="checkout"]');
    if (checkoutButton) return redirectToCheckout(event);

    const noteApplyButton = target.closest?.('cart-note button[type="button"]');
    if (noteApplyButton) {
      event.preventDefault();
      return applyCartNote(noteApplyButton);
    }

    const removeButton = target.closest?.('[data-storefront-remove]');
    if (removeButton) return removeLine(removeButton.dataset.storefrontLineId, removeButton);

    const minusButton = target.closest?.('[data-storefront-line-minus]');
    if (minusButton) {
      event.preventDefault();
      const { lineId, input } = getLineContextFromQuantityButton(minusButton);
      const quantity = Math.max((Number.parseInt(input?.value || '1', 10) || 1) - 1, 1);
      return updateLineQuantity(lineId, quantity, minusButton, 'minus');
    }

    const plusButton = target.closest?.('[data-storefront-line-plus]');
    if (plusButton) {
      event.preventDefault();
      const { lineId } = getLineContextFromQuantityButton(plusButton);
      return incrementLineFromButton(lineId, plusButton);
    }

    const discountRemove = target.closest?.('[data-storefront-discount-remove], [is="discount-remove"]');
    if (discountRemove) {
      event.preventDefault();
      return removeDiscount(discountRemove.dataset.discount, discountRemove);
    }
  }

  function handleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (form.matches('form[is="product-form"]')) {
      return handleProductFormSubmit(event, form);
    }

    if (form.matches('form[is="cart-discount"]')) {
      return applyDiscountForm(event, form);
    }

    if (event.submitter?.name === 'checkout' || form.querySelector('[name="checkout"]')) {
      return redirectToCheckout(event);
    }
  }

  function handleChange(event) {
    const quantityInput = event.target.closest?.('[data-storefront-quantity-input]');
    if (quantityInput) {
      const lineId = quantityInput.dataset.storefrontLineId;
      const quantity = Math.max(Number.parseInt(quantityInput.value || '1', 10) || 1, 1);
      debounceLineQuantity(lineId, quantity, quantityInput);
      return;
    }

    const noteInput = event.target.closest?.('cart-note textarea[name="note"]');
    if (noteInput) {
      clearTimeout(debouncedTimers.get('cart-note'));
      debouncedTimers.set('cart-note', setTimeout(async () => {
        debouncedTimers.delete('cart-note');
        try {
          const cart = await api.updateNote(noteInput.value);
          renderCart(cart);
        }
        catch (error) {
          showError(error.message);
        }
      }, 300));
    }
  }

  defineCartDrawer();
  defineNoopElement('cart-items');
  defineNoopElement('main-cart');
  defineNoopElement('cart-note');
  defineButtonElement('add-to-cart-button', false);
  defineButtonElement('buy-now-button', true);

  document.addEventListener('submit', handleSubmit, true);
  document.addEventListener('click', handleClick, true);
  document.addEventListener('change', handleChange, true);
  document.addEventListener('storefront-cart:updated', (event) => renderCart(event.detail?.cart));
  document.addEventListener('DOMContentLoaded', () => refreshCart().catch(() => {}));
})(typeof window !== 'undefined' ? window : globalThis);
